/**
 * Content-to-Service Mapping Configuration
 *
 * Maps content categories (KB, blog, tools) to relevant services
 * for contextual CTA generation. Based on traffic analysis showing
 * 95% of traffic goes to content pages, not service pages.
 */

export interface ServiceMapping {
  /** Primary service slug */
  primaryService: string;
  /** Primary service display name */
  primaryServiceName: string;
  /** Secondary service slug (optional) */
  secondaryService?: string;
  /** Secondary service display name (optional) */
  secondaryServiceName?: string;
  /** CTA title */
  title: string;
  /** CTA description */
  description: string;
}

/**
 * Knowledge Base Category to Service Mappings
 *
 * CrowdStrike content drives ~5,000 sessions/month from IT admins
 * and security professionals - perfect audience for MDR/security services.
 */
export const kbCategoryMappings: Record<string, ServiceMapping> = {
  // ============================================
  // AI CLI TOOLS (Developers using AI coding assistants)
  // ============================================
  claude: {
    primaryService: 'secure-mvp',
    primaryServiceName: 'Secure MVP',
    secondaryService: 'ai-security-audit',
    secondaryServiceName: 'AI Security Audit',
    title: 'Stuck shipping with Claude Code?',
    description:
      "I help founders ship the SaaS apps they've been building with AI. 8 shipped — yours could be next, in 6 weeks. Auth, payments, and security baked in.",
  },
  gemini: {
    primaryService: 'secure-mvp',
    primaryServiceName: 'Secure MVP',
    secondaryService: 'cloud-infrastructure-management',
    secondaryServiceName: 'Cloud Infrastructure & Optimization',
    title: 'Building with Gemini CLI?',
    description:
      "I ship production-ready SaaS apps in 6 weeks. Or if you're scaling GCP infrastructure, we cover that too.",
  },
  openai: {
    primaryService: 'secure-mvp',
    primaryServiceName: 'Secure MVP',
    secondaryService: 'software-development',
    secondaryServiceName: 'Custom Software Development',
    title: 'Building with the OpenAI API?',
    description:
      "I help founders ship AI-powered SaaS apps to production. 8 shipped, secure by default, 6 weeks to launch.",
  },
  copilot: {
    primaryService: 'secure-mvp',
    primaryServiceName: 'Secure MVP',
    secondaryService: 'ai-security-audit',
    secondaryServiceName: 'AI Security Audit',
    title: 'Shipping with Copilot? Get the last 30% done.',
    description:
      "Copilot got you most of the way. The last 30% — auth that doesn't leak, payments that don't break, security that doesn't bite — is where I come in.",
  },

  // ============================================
  // MICROSOFT SECURITY STACK
  // ============================================
  intune: {
    primaryService: 'managed-services',
    primaryServiceName: 'Managed IT Services',
    secondaryService: '24-7-detection-and-response',
    secondaryServiceName: '24/7 Detection & Response',
    title: 'Need Expert Device Management?',
    description:
      "Our team manages Intune deployments, device compliance policies, and endpoint security for organizations of all sizes.",
  },
  defender: {
    primaryService: '24-7-detection-and-response',
    primaryServiceName: '24/7 Detection & Response',
    secondaryService: 'vulnerability-management',
    secondaryServiceName: 'Vulnerability Management',
    title: 'Maximize Your Defender Investment',
    description:
      "Our MDR team monitors your Microsoft Defender environment 24/7 and responds to threats before they become incidents.",
  },
  sentinel: {
    primaryService: '24-7-detection-and-response',
    primaryServiceName: '24/7 Detection & Response',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance & Risk Assessment',
    title: 'Need Sentinel Expertise?',
    description:
      "Our team builds detection rules, automates playbooks, and provides 24/7 monitoring for Microsoft Sentinel deployments.",
  },

  // ============================================
  // EMAIL & IDENTITY SECURITY
  // ============================================
  proofpoint: {
    primaryService: 'small-business-cybersecurity',
    primaryServiceName: 'Small Business Cybersecurity',
    secondaryService: 'security-awareness',
    secondaryServiceName: 'Security Awareness Training',
    title: 'Expert Email Security Management',
    description:
      "Our team configures, tunes, and manages Proofpoint to stop phishing, BEC, and data exfiltration threats.",
  },
  '1password': {
    primaryService: 'vciso',
    primaryServiceName: 'vCISO Services',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance Services',
    title: 'Enterprise Credential Strategy',
    description:
      "Our vCISO service helps implement credential management policies aligned with compliance frameworks and security best practices.",
  },

  // ============================================
  // NETWORK & ENDPOINT SECURITY
  // ============================================
  checkpoint: {
    primaryService: 'email-security',
    primaryServiceName: 'Managed Email Security',
    secondaryService: '24-7-detection-and-response',
    secondaryServiceName: '24/7 Detection & Response',
    title: 'Skip the Harmony Setup — Let Us Manage It',
    description:
      "These Check Point Harmony how-tos are the hard way. Our managed email security service deploys, tunes, and monitors Harmony email for you — phishing, BEC, and DLP handled.",
  },

  // ============================================
  // ENDPOINT DETECTION & RESPONSE
  // ============================================
  crowdstrike: {
    primaryService: '24-7-detection-and-response',
    primaryServiceName: '24/7 Detection & Response',
    secondaryService: '24-7-detection-and-response',
    secondaryServiceName: 'EDR Migration & Hardening',
    title: 'Need Expert CrowdStrike Management?',
    description:
      "Whether you're migrating EDR platforms or need managed detection, our team handles seamless transitions and 24/7 monitoring.",
  },
  microsoft: {
    primaryService: 'managed-services',
    primaryServiceName: 'Managed IT Services',
    secondaryService: '24-7-detection-and-response',
    secondaryServiceName: '24/7 Detection & Response',
    title: 'Struggling with Microsoft Updates?',
    description:
      "Let our team handle Windows patching, monitoring, and system management so you can focus on your business.",
  },
  'microsoft-365': {
    primaryService: 'productivity-suite-management',
    primaryServiceName: 'Productivity Suite Management',
    secondaryService: 'small-business-cybersecurity',
    secondaryServiceName: 'Small Business Cybersecurity',
    title: 'Need Expert Microsoft 365 Support?',
    description:
      "Our team handles M365 setup, security hardening, migration, and ongoing management so you can focus on your business.",
  },
  'google-workspace': {
    primaryService: 'productivity-suite-management',
    primaryServiceName: 'Productivity Suite Management',
    secondaryService: 'small-business-cybersecurity',
    secondaryServiceName: 'Small Business Cybersecurity',
    title: 'Need Expert Google Workspace Support?',
    description:
      "Expert configuration, security hardening, and ongoing management for Google Workspace.",
  },
  aws: {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    secondaryService: 'devsecops',
    secondaryServiceName: 'DevSecOps Services',
    title: 'Need AWS Infrastructure Expertise?',
    description:
      "Multi-cloud architecture, migration, FinOps cost optimization, and 24/7 managed operations across AWS environments.",
  },
  'google-cloud': {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    secondaryService: 'devops-consulting',
    secondaryServiceName: 'DevOps Consulting',
    title: 'Expert GCP Management',
    description:
      "From architecture design to FinOps optimization to managed operations, we handle your Google Cloud infrastructure.",
  },
  'microsoft-azure': {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    secondaryService: 'devops-consulting',
    secondaryServiceName: 'DevOps Consulting',
    title: 'Azure Infrastructure Experts',
    description:
      "Comprehensive Azure management including architecture, migration, FinOps optimization, security, and 24/7 operations.",
  },
  'security-compliance': {
    primaryService: 'compliance',
    primaryServiceName: 'Compliance & Risk Assessment',
    secondaryService: 'vciso',
    secondaryServiceName: 'vCISO Services',
    title: 'Need Security Compliance Expertise?',
    description:
      'Navigate compliance frameworks and risk management with our expert security team. From assessments to ongoing oversight.',
  },
};

/**
 * Tool Category to Service Mappings
 *
 * Security tools attract security-conscious users,
 * developer tools attract tech professionals who influence purchasing.
 */
export const toolCategoryMappings: Record<string, ServiceMapping> = {
  security: {
    primaryService: 'secure-mvp',
    primaryServiceName: 'Secure MVP',
    secondaryService: 'penetration-testing',
    secondaryServiceName: 'Penetration Testing',
    title: 'Building something secure?',
    description:
      "I ship production-ready SaaS apps in 6 weeks — built secure from day one by someone who knows how attackers think. Or get a pen test if you already shipped.",
  },
  cryptography: {
    primaryService: 'devsecops',
    primaryServiceName: 'DevSecOps Services',
    secondaryService: 'penetration-testing',
    secondaryServiceName: 'Penetration Testing',
    title: 'Secure Your Encryption Implementation',
    description:
      "Cryptography is complex. Our security experts review and harden crypto implementations as part of a secure SDLC.",
  },
  encoding: {
    primaryService: 'secure-mvp',
    primaryServiceName: 'Secure MVP',
    secondaryService: 'software-development',
    secondaryServiceName: 'Custom Software Development',
    title: 'Building Something Bigger?',
    description:
      "I ship production-ready SaaS apps in 6 weeks. Or scale beyond MVP with custom development.",
  },
  developer: {
    primaryService: 'secure-mvp',
    primaryServiceName: 'Secure MVP',
    secondaryService: 'software-development',
    secondaryServiceName: 'Custom Software Development',
    title: "You build the idea. I'll ship the product.",
    description:
      "Productized MVP development for founders. 9 SaaS apps shipped — yours could be next, in 6 weeks. Secure by default.",
  },
  networking: {
    primaryService: 'managed-services',
    primaryServiceName: 'Managed IT Services',
    secondaryService: 'cloud-infrastructure-management',
    secondaryServiceName: 'Cloud Infrastructure & Optimization',
    title: 'Optimize Your Network',
    description:
      "Our team can assess, optimize, and monitor your network and cloud infrastructure 24/7.",
  },
  planning: {
    primaryService: 'vciso',
    primaryServiceName: 'vCISO Services',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance & Risk Assessment',
    title: 'Strategic Security Planning',
    description:
      "Get C-level security guidance to align your security investments with business goals.",
  },
  compliance: {
    primaryService: 'compliance',
    primaryServiceName: 'Compliance & Risk Assessment',
    secondaryService: 'vciso',
    secondaryServiceName: 'vCISO Services',
    title: 'Simplify Compliance',
    description:
      "Navigate HIPAA, SOC 2, NIST, and other regulations with expert guidance.",
  },
  analysis: {
    primaryService: 'compliance',
    primaryServiceName: 'Compliance & Risk Assessment',
    secondaryService: 'vulnerability-management',
    secondaryServiceName: 'Vulnerability Management',
    title: 'Deep Dive into Your Security Posture',
    description:
      "Our analysts identify risks and provide actionable recommendations to protect your business.",
  },
  conversion: {
    primaryService: 'secure-mvp',
    primaryServiceName: 'Secure MVP',
    secondaryService: 'software-development',
    secondaryServiceName: 'Custom Software Development',
    title: 'Automation & Integration',
    description:
      "Building automation or integrations? I ship production-ready SaaS apps in 6 weeks.",
  },
  math: {
    primaryService: 'secure-mvp',
    primaryServiceName: 'Secure MVP',
    secondaryService: 'software-development',
    secondaryServiceName: 'Custom Software Development',
    title: 'Complex Calculations at Scale',
    description:
      "Need a custom calculator or data-processing SaaS? I ship production-ready apps in 6 weeks.",
  },
};

/**
 * Blog Category to Service Mappings
 *
 * Complements existing blog-ctas.ts with more specific mappings.
 * Used when blog post doesn't have a custom CTA defined.
 */
export const blogCategoryMappings: Record<string, ServiceMapping> = {
  // ============================================
  // DEV-SHAPED (push Secure MVP — the pivot offer)
  // ============================================
  'web-development': {
    primaryService: 'secure-mvp',
    primaryServiceName: 'Secure MVP',
    secondaryService: 'software-development',
    secondaryServiceName: 'Custom Software Development',
    title: 'Need help shipping your web app?',
    description:
      "Productized MVP development for founders — secure by default, shipped in 6 weeks. Or scale beyond MVP with custom development.",
  },
  'developer-tools': {
    primaryService: 'secure-mvp',
    primaryServiceName: 'Secure MVP',
    secondaryService: 'software-development',
    secondaryServiceName: 'Custom Software Development',
    title: "Building something with these tools?",
    description:
      "I've shipped 9 SaaS apps myself. I'll ship yours in 6 weeks — auth, payments, and security baked in.",
  },
  development: {
    primaryService: 'secure-mvp',
    primaryServiceName: 'Secure MVP',
    secondaryService: 'software-development',
    secondaryServiceName: 'Custom Software Development',
    title: 'Need a developer who actually ships?',
    description:
      "Productized MVP development for founders. Fixed scope, 6 weeks to production, you own all the code.",
  },
  'software-engineering': {
    primaryService: 'secure-mvp',
    primaryServiceName: 'Secure MVP',
    secondaryService: 'software-development',
    secondaryServiceName: 'Custom Software Development',
    title: 'Need a senior engineer for your MVP?',
    description:
      "Productized MVP development by an engineer who's shipped 9 SaaS apps and knows how attackers think.",
  },
  python: {
    primaryService: 'secure-mvp',
    primaryServiceName: 'Secure MVP',
    secondaryService: 'software-development',
    secondaryServiceName: 'Custom Software Development',
    title: 'Building a Python app?',
    description:
      "I ship production-ready SaaS apps in 6 weeks. Secure by default, your code, no lock-in.",
  },
  'software-development': {
    primaryService: 'secure-mvp',
    primaryServiceName: 'Secure MVP',
    secondaryService: 'software-development',
    secondaryServiceName: 'Custom Software Development',
    title: 'Want yours shipped next?',
    description:
      "Productized MVP development for founders. 9 SaaS apps shipped — yours could be next, in 6 weeks.",
  },
  automation: {
    primaryService: 'secure-mvp',
    primaryServiceName: 'Secure MVP',
    secondaryService: 'devops-consulting',
    secondaryServiceName: 'DevOps Consulting',
    title: 'Building automation? Build it right.',
    description:
      "I ship production-ready automation and SaaS apps — secure, scalable, 6 weeks to production.",
  },
  'web-design': {
    primaryService: 'secure-mvp',
    primaryServiceName: 'Secure MVP',
    secondaryService: 'software-development',
    secondaryServiceName: 'Custom Software Development',
    title: "Got a design? Let's ship the product.",
    description:
      "Productized MVP development — turn your design into a working SaaS in 6 weeks.",
  },

  // ============================================
  // CLOUD-shaped
  // ============================================
  cloud: {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    secondaryService: 'devops-consulting',
    secondaryServiceName: 'DevOps Consulting',
    title: 'Cloud expertise for AWS, Azure, GCP',
    description:
      "Multi-cloud architecture, migration, FinOps optimization, and 24/7 managed operations.",
  },
  'cloud-security': {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    secondaryService: 'vciso',
    secondaryServiceName: 'vCISO Services',
    title: 'Secure your cloud before misconfigs become breaches',
    description:
      "Cloud security assessments mapped to CIS Benchmarks, NIST CSF, SOC 2, HIPAA, and PCI-DSS — with prioritized remediation.",
  },
  'cloud-&-devops': {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    secondaryService: 'devops-consulting',
    secondaryServiceName: 'DevOps Consulting',
    title: 'Cloud + DevOps expertise',
    description:
      "AWS, Azure, GCP architecture plus CI/CD pipelines, IaC, and Kubernetes — covered end-to-end.",
  },

  // ============================================
  // IT / MANAGED-services-shaped
  // ============================================
  powershell: {
    primaryService: 'managed-services',
    primaryServiceName: 'Managed IT Services',
    secondaryService: '24-7-detection-and-response',
    secondaryServiceName: '24/7 Detection & Response',
    title: 'Automate Windows Management',
    description:
      "Our managed services team handles patching, updates, monitoring, and automation so you can focus on strategic work.",
  },
  windows: {
    primaryService: 'managed-services',
    primaryServiceName: 'Managed IT Services',
    secondaryService: '24-7-detection-and-response',
    secondaryServiceName: '24/7 Detection & Response',
    title: 'Windows Infrastructure Experts',
    description:
      "From patching to monitoring, we keep your Windows environment secure and running smoothly.",
  },
  networking: {
    primaryService: 'managed-services',
    primaryServiceName: 'Managed IT Services',
    secondaryService: '24-7-detection-and-response',
    secondaryServiceName: '24/7 Detection & Response',
    title: 'Network infrastructure managed end-to-end',
    description:
      "24/7 monitoring, performance optimization, and security across your network infrastructure.",
  },
  virtualization: {
    primaryService: 'managed-services',
    primaryServiceName: 'Managed IT Services',
    secondaryService: 'cloud-infrastructure-management',
    secondaryServiceName: 'Cloud Infrastructure & Optimization',
    title: 'Virtualization & infrastructure experts',
    description:
      "VMware, Hyper-V, and cloud-native virtualization — managed, monitored, and optimized.",
  },
  storage: {
    primaryService: 'managed-services',
    primaryServiceName: 'Managed IT Services',
    secondaryService: 'backup-and-ransomware-defense',
    secondaryServiceName: 'Backup & Disaster Recovery',
    title: 'Storage and backup, done right',
    description:
      "Resilient storage architecture, backup strategy, and disaster recovery for businesses that can't lose data.",
  },
  'data-management': {
    primaryService: 'managed-services',
    primaryServiceName: 'Managed IT Services',
    secondaryService: 'cloud-infrastructure-management',
    secondaryServiceName: 'Cloud Infrastructure & Optimization',
    title: 'Data management & cloud infrastructure',
    description:
      "Data strategy, storage, and migration across on-premises and cloud — managed by experts.",
  },

  // ============================================
  // CYBER-shaped (push small-business-cybersecurity / vciso / 24-7)
  // ============================================
  cybersecurity: {
    primaryService: 'small-business-cybersecurity',
    primaryServiceName: 'Small Business Cybersecurity',
    secondaryService: 'vciso',
    secondaryServiceName: 'vCISO Services',
    title: 'Cybersecurity for growing businesses',
    description:
      "Enterprise-grade protection at SMB pricing. vCISO leadership, 24/7 detection, compliance support, and security awareness training.",
  },
  security: {
    primaryService: 'small-business-cybersecurity',
    primaryServiceName: 'Small Business Cybersecurity',
    secondaryService: 'vciso',
    secondaryServiceName: 'vCISO Services',
    title: 'Need security expertise?',
    description:
      "Enterprise-grade security for small and mid-sized businesses. From vCISO advisory to 24/7 monitoring.",
  },
  'security-tools': {
    primaryService: 'small-business-cybersecurity',
    primaryServiceName: 'Small Business Cybersecurity',
    secondaryService: 'penetration-testing',
    secondaryServiceName: 'Penetration Testing',
    title: 'Tools alone are not a security program',
    description:
      "We help you build a complete security program — strategy, monitoring, testing, and ongoing advisory.",
  },
  'mdr-security': {
    primaryService: '24-7-detection-and-response',
    primaryServiceName: '24/7 Detection & Response',
    secondaryService: 'vciso',
    secondaryServiceName: 'vCISO Services',
    title: 'Managed Detection & Response',
    description:
      "24/7 threat detection and response powered by CrowdStrike Complete and expert analysts.",
  },
  'email-security': {
    primaryService: 'small-business-cybersecurity',
    primaryServiceName: 'Small Business Cybersecurity',
    secondaryService: 'vciso',
    secondaryServiceName: 'vCISO Services',
    title: 'Stop email threats at the inbox',
    description:
      "Advanced phishing protection, BEC prevention, DLP, and encryption — part of our SMB cybersecurity program.",
  },
  compliance: {
    primaryService: 'compliance',
    primaryServiceName: 'Compliance & Risk Assessment',
    secondaryService: 'vciso',
    secondaryServiceName: 'vCISO Services',
    title: 'Compliance done by people who get it',
    description:
      "HIPAA, SOC 2, PCI-DSS, ISO 27001, NIST CSF — gap assessments, risk analysis, policy development, audit prep.",
  },
  'risk-management': {
    primaryService: 'compliance',
    primaryServiceName: 'Compliance & Risk Assessment',
    secondaryService: 'vciso',
    secondaryServiceName: 'vCISO Services',
    title: 'Know your risks, protect your business',
    description:
      "Comprehensive risk assessments tied to business impact, with prioritized remediation roadmaps.",
  },

  // ============================================
  // DEV/CYBER hybrid
  // ============================================
  cryptography: {
    primaryService: 'devsecops',
    primaryServiceName: 'DevSecOps Services',
    secondaryService: 'penetration-testing',
    secondaryServiceName: 'Penetration Testing',
    title: 'Cryptography is complex — get it right',
    description:
      "Our security experts review and harden crypto implementations as part of a secure SDLC.",
  },
  'web-security': {
    primaryService: 'devsecops',
    primaryServiceName: 'DevSecOps Services',
    secondaryService: 'penetration-testing',
    secondaryServiceName: 'Penetration Testing',
    title: 'Web security from build to run',
    description:
      "OWASP best practices baked into your SDLC, plus pen testing to validate before launch.",
  },
  'secrets-management': {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    secondaryService: 'vciso',
    secondaryServiceName: 'vCISO Services',
    title: 'Secure Your Secrets',
    description:
      "Proper secrets management is critical. Our cloud and security teams can assess and improve your practices.",
  },
  // Backwards-compat: old category slug used before standardization
  secrets: {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    secondaryService: 'vciso',
    secondaryServiceName: 'vCISO Services',
    title: 'Secure Your Secrets',
    description:
      "Proper secrets management is critical. Our cloud and security teams can assess and improve your practices.",
  },

  // ============================================
  // EXISTING (kept — labels were already correct)
  // ============================================
  'endpoint-security': {
    primaryService: '24-7-detection-and-response',
    primaryServiceName: '24/7 Detection & Response',
    secondaryService: 'vulnerability-management',
    secondaryServiceName: 'Vulnerability Management',
    title: 'Comprehensive Endpoint Protection',
    description:
      "Our MDR service combines CrowdStrike with expert analysts for 24/7 threat detection and response.",
  },
  devops: {
    primaryService: 'devops-consulting',
    primaryServiceName: 'DevOps Consulting',
    secondaryService: 'devsecops',
    secondaryServiceName: 'DevSecOps Services',
    title: 'DevOps & DevSecOps Experts',
    description:
      "From CI/CD pipelines to infrastructure as code, our DevOps consultants accelerate your delivery.",
  },
};

/**
 * Specific Page Mappings (High-Traffic Pages)
 *
 * Custom mappings for individual high-traffic pages.
 * Takes precedence over category mappings.
 */
export const specificPageMappings: Record<string, ServiceMapping> = {
  // ============================================
  // KNOWLEDGE BASE ARTICLES
  // ============================================

  // Top CrowdStrike KB articles (5,000+ sessions/month combined)
  'how-to-uninstall-crowdstrike-falcon-sensor': {
    primaryService: '24-7-detection-and-response',
    primaryServiceName: 'EDR Migration & Hardening',
    secondaryService: '24-7-detection-and-response',
    secondaryServiceName: '24/7 Detection & Response',
    title: 'Switching EDR Solutions?',
    description:
      "Don't leave security gaps during migration. Our team handles scripted agent removal, new deployment, and 30-day tuning.",
  },
  'how-to-run-an-on-demand-scan-in-crowdstrike': {
    primaryService: 'vulnerability-management',
    primaryServiceName: 'Vulnerability Management',
    secondaryService: '24-7-detection-and-response',
    secondaryServiceName: '24/7 Detection & Response',
    title: 'Automate Threat Detection',
    description:
      "Manual scanning is time-consuming. Our managed vulnerability service provides continuous protection.",
  },
  'how-to-verify-crowdstrike-falcon-sensor': {
    primaryService: '24-7-detection-and-response',
    primaryServiceName: '24/7 Detection & Response',
    title: 'Ensure Complete Protection',
    description:
      "Our MDR team monitors your CrowdStrike deployment and responds to threats 24/7.",
  },
  'how-to-setup-crowdstrike-device-control': {
    primaryService: 'vciso',
    primaryServiceName: 'vCISO Services',
    secondaryService: '24-7-detection-and-response',
    secondaryServiceName: '24/7 Detection & Response',
    title: 'Need Security Policy Guidance?',
    description:
      "Device control is just one piece. Our vCISO service helps build comprehensive security policies.",
  },
  'creating-and-managing-user-roles': {
    primaryService: 'vciso',
    primaryServiceName: 'vCISO Services',
    title: 'Strategic Access Management',
    description:
      "Role-based access is critical for security. Our vCISO service helps design and implement access policies.",
  },
  'deploying-falcon-sensor-using-gpo': {
    primaryService: '24-7-detection-and-response',
    primaryServiceName: '24/7 Detection & Response',
    secondaryService: 'managed-services',
    secondaryServiceName: 'Managed IT Services',
    title: 'Enterprise Deployment Expertise',
    description:
      "Our team deploys and manages CrowdStrike across organizations of all sizes with 24/7 monitoring.",
  },

  // AI CLI Tools - Enterprise & Integration Articles
  'how-to-install-claude-code-cli': {
    primaryService: 'ai-security-audit',
    primaryServiceName: 'AI Security Audit',
    secondaryService: 'devsecops',
    secondaryServiceName: 'DevSecOps Services',
    title: 'Using AI Coding Tools? Audit Your Exposure.',
    description:
      "Our AI Security Audit identifies data leakage risks from AI coding assistants and implements enterprise security policies.",
  },
  'how-to-set-up-mcp-servers': {
    primaryService: 'ai-security-audit',
    primaryServiceName: 'AI Security Audit',
    secondaryService: 'devsecops',
    secondaryServiceName: 'DevSecOps Services',
    title: 'MCP Servers Need Security Review',
    description:
      "MCP servers extend AI capabilities but need proper security. Our team implements secure integrations with access controls.",
  },
  'how-to-set-up-enterprise': {
    primaryService: 'vciso',
    primaryServiceName: 'vCISO Services',
    secondaryService: 'devops-consulting',
    secondaryServiceName: 'DevOps Consulting',
    title: 'Enterprise AI Tool Governance?',
    description:
      "Our vCISO team helps organizations develop AI usage policies, access controls, and security guidelines for developer tools.",
  },
  'how-to-set-up-sso-saml': {
    primaryService: 'vciso',
    primaryServiceName: 'vCISO Services',
    secondaryService: 'productivity-suite-management',
    secondaryServiceName: 'Productivity Suite Management',
    title: 'Implementing SSO for Dev Tools?',
    description:
      "Our team implements enterprise SSO, SAML federation, and identity governance across your development stack.",
  },
  'how-to-integrate-cicd': {
    primaryService: 'devsecops',
    primaryServiceName: 'DevSecOps Services',
    secondaryService: 'devops-consulting',
    secondaryServiceName: 'DevOps Consulting',
    title: 'AI-Assisted CI/CD Pipelines?',
    description:
      "Our DevSecOps team integrates AI tools into secure CI/CD pipelines with proper guardrails and audit trails.",
  },
  'how-to-set-up-vertex-ai-enterprise': {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    secondaryService: 'devsecops',
    secondaryServiceName: 'DevSecOps Services',
    title: 'Enterprise Vertex AI Deployment?',
    description:
      "Our Google Cloud experts configure Vertex AI with proper IAM, VPC controls, and compliance requirements.",
  },

  // Check Point - Email Security Integration Articles
  'how-to-connect-harmony-email-microsoft-365': {
    primaryService: 'small-business-cybersecurity',
    primaryServiceName: 'Small Business Cybersecurity',
    secondaryService: 'productivity-suite-management',
    secondaryServiceName: 'Productivity Suite Management',
    title: 'Securing Microsoft 365 Email?',
    description:
      "Our team deploys and manages advanced email security for M365 with threat emulation and anti-phishing protection.",
  },
  'how-to-connect-harmony-email-google-workspace': {
    primaryService: 'small-business-cybersecurity',
    primaryServiceName: 'Small Business Cybersecurity',
    secondaryService: 'productivity-suite-management',
    secondaryServiceName: 'Productivity Suite Management',
    title: 'Securing Google Workspace Email?',
    description:
      "Our team deploys and manages advanced email security for Google Workspace with threat emulation and anti-phishing.",
  },
  'how-to-configure-harmony-email-threat-emulation': {
    primaryService: 'small-business-cybersecurity',
    primaryServiceName: 'Small Business Cybersecurity',
    secondaryService: '24-7-detection-and-response',
    secondaryServiceName: '24/7 Detection & Response',
    title: 'Advanced Email Threat Protection?',
    description:
      "Our security team configures and monitors advanced threat emulation to catch zero-day attacks and sophisticated phishing.",
  },
  'how-to-configure-harmony-email-anti-phishing': {
    primaryService: 'small-business-cybersecurity',
    primaryServiceName: 'Small Business Cybersecurity',
    secondaryService: 'security-awareness',
    secondaryServiceName: 'Security Awareness Training',
    title: 'Stopping Phishing Attacks?',
    description:
      "Our team implements multi-layered anti-phishing protection combining technical controls with user awareness training.",
  },
  'how-to-configure-harmony-email-dlp': {
    primaryService: 'compliance',
    primaryServiceName: 'Compliance Services',
    secondaryService: 'small-business-cybersecurity',
    secondaryServiceName: 'Small Business Cybersecurity',
    title: 'Preventing Email Data Leaks?',
    description:
      "Our compliance team implements DLP policies to protect sensitive data and meet regulatory requirements.",
  },
  'how-to-deploy-harmony-endpoint-agent': {
    primaryService: '24-7-detection-and-response',
    primaryServiceName: '24/7 Detection & Response',
    secondaryService: 'managed-services',
    secondaryServiceName: 'Managed IT Services',
    title: 'Enterprise Endpoint Deployment?',
    description:
      "Our team deploys and manages endpoint protection across your organization with 24/7 threat monitoring.",
  },

  // Microsoft Security Stack Articles
  'how-to-onboard-devices': {
    primaryService: 'managed-services',
    primaryServiceName: 'Managed IT Services',
    secondaryService: '24-7-detection-and-response',
    secondaryServiceName: '24/7 Detection & Response',
    title: 'Onboarding Devices to Defender?',
    description:
      "Our team manages device onboarding, compliance policies, and ongoing monitoring for Microsoft Defender.",
  },
  'how-to-configure-asr-rules': {
    primaryService: 'vciso',
    primaryServiceName: 'vCISO Services',
    secondaryService: '24-7-detection-and-response',
    secondaryServiceName: '24/7 Detection & Response',
    title: 'Attack Surface Reduction Strategy?',
    description:
      "Our security team designs ASR policies that maximize protection without disrupting business operations.",
  },
  'how-to-investigate-incidents': {
    primaryService: '24-7-detection-and-response',
    primaryServiceName: '24/7 Detection & Response',
    secondaryService: 'vciso',
    secondaryServiceName: 'vCISO Services',
    title: 'Incident Investigation Overwhelming?',
    description:
      "Our SOC team investigates, contains, and remediates security incidents 24/7 so your team can focus on business.",
  },
  'how-to-connect-data-sources': {
    primaryService: '24-7-detection-and-response',
    primaryServiceName: '24/7 Detection & Response',
    secondaryService: 'managed-services',
    secondaryServiceName: '24/7 IT Monitoring',
    title: 'SIEM Data Integration Challenges?',
    description:
      "Our SOC team connects data sources, normalizes logs, and builds comprehensive security visibility.",
  },
  'how-to-create-analytics-rules': {
    primaryService: '24-7-detection-and-response',
    primaryServiceName: '24/7 Detection & Response',
    secondaryService: '24-7-detection-and-response',
    secondaryServiceName: '24/7 Detection & Response',
    title: 'Building Detection Rules?',
    description:
      "Our SOC engineers build custom analytics rules tuned to your environment with minimal false positives.",
  },
  'how-to-set-up-playbooks': {
    primaryService: 'vciso',
    primaryServiceName: 'vCISO Services',
    secondaryService: '24-7-detection-and-response',
    secondaryServiceName: '24/7 Detection & Response',
    title: 'Automating Incident Response?',
    description:
      "Our team designs and implements SOAR playbooks that accelerate response times and ensure consistency.",
  },

  // ============================================
  // BLOG POSTS
  // ============================================

  'windows-update-commands-powershell': {
    primaryService: 'managed-services',
    primaryServiceName: 'Managed IT Services',
    secondaryService: 'managed-services',
    secondaryServiceName: '24/7 IT Monitoring',
    title: 'Tired of Manual Patching?',
    description:
      "Our patch management service automates Windows updates across your entire infrastructure.",
  },
  'how-to-install-curl-on-windows': {
    primaryService: 'software-development',
    primaryServiceName: 'Software Development',
    title: 'Building API Integrations?',
    description:
      "Our developers build custom integrations, APIs, and automation solutions.",
  },
  'hashicorp-vault-cli-install-and-gui-setup': {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    secondaryService: 'vciso',
    secondaryServiceName: 'vCISO Services',
    title: 'Secrets Management Done Right',
    description:
      "Our security team can assess your secrets management practices and implement enterprise-grade solutions.",
  },
  'discord-webhooks-guide': {
    primaryService: 'software-development',
    primaryServiceName: 'Software Development',
    title: 'Need Custom Integrations?',
    description:
      "From webhooks to full applications, our team builds secure, scalable solutions.",
  },

  // ============================================
  // ENDPOINT SECURITY & MDR/EDR BLOG POSTS
  // ============================================

  'crowdstrike-mdr-service-24-7-threat-protection': {
    primaryService: '24-7-detection-and-response',
    primaryServiceName: '24/7 Detection & Response',
    secondaryService: 'vulnerability-management',
    secondaryServiceName: 'Vulnerability Management',
    title: 'Ready for 24/7 Threat Protection?',
    description:
      "Our MDR team combines CrowdStrike expertise with round-the-clock monitoring to protect your business.",
  },
  'choosing-between-mdr-edr-mssp-xdr-and-soc-your-complete-guide': {
    primaryService: '24-7-detection-and-response',
    primaryServiceName: '24/7 Detection & Response',
    title: 'Still Comparing Security Solutions?',
    description:
      "Let our experts help you choose the right combination of MDR, EDR, and SOC services for your business.",
  },
  'edr-vs-mdr-what-small-businesses-really-need-to-know': {
    primaryService: '24-7-detection-and-response',
    primaryServiceName: '24/7 Detection & Response',
    title: 'Get Enterprise Security Without Enterprise Costs',
    description:
      "Our MDR service gives SMBs 24/7 threat detection and response at a fraction of in-house SOC costs.",
  },
  'edr-mdr-platform-comparison-guide-find-your-perfect-security-solution': {
    primaryService: '24-7-detection-and-response',
    primaryServiceName: '24/7 Detection & Response',
    title: 'Let Us Handle the Comparison',
    description:
      "Our team evaluates and deploys the right EDR/MDR platform for your environment with ongoing management.",
  },
  'edr-vs-antivirus-why-traditional-security-isnt-enough': {
    primaryService: '24-7-detection-and-response',
    primaryServiceName: '24/7 Detection & Response',
    title: 'Upgrade to Real Protection',
    description:
      "Antivirus can't stop modern attacks. Our MDR service provides the advanced detection your business needs.",
  },
  'elevate-your-security-posture-with-managed-detection-and-response': {
    primaryService: '24-7-detection-and-response',
    primaryServiceName: '24/7 Detection & Response',
    title: 'Elevate Your Security Today',
    description:
      "Our MDR experts are ready to protect your business with 24/7 monitoring and rapid threat response.",
  },
  'crowdstrike-vs-arctic-wolf-mdr-comparison': {
    primaryService: '24-7-detection-and-response',
    primaryServiceName: '24/7 Detection & Response',
    title: 'Need Help Choosing?',
    description:
      "Our team deploys and manages leading MDR platforms. Let us recommend the right solution for your needs.",
  },
  'crowdstrike-vs-cylance-endpoint-security-comparison': {
    primaryService: '24-7-detection-and-response',
    primaryServiceName: '24/7 Detection & Response',
    title: 'Expert Endpoint Security Management',
    description:
      "We deploy and manage endpoint security platforms with 24/7 monitoring and threat response.",
  },
  'crowdstrike-vs-huntress-which-endpoint-security-platform-is-right-for-your-smb': {
    primaryService: '24-7-detection-and-response',
    primaryServiceName: '24/7 Detection & Response',
    title: 'Get the Right Solution for Your SMB',
    description:
      "Our team helps SMBs select and deploy the right endpoint security platform with managed detection.",
  },

  // ============================================
  // EMAIL SECURITY BLOG POSTS
  // ============================================

  'spf-dkim-email-authentication-implementation': {
    primaryService: 'small-business-cybersecurity',
    primaryServiceName: 'Small Business Cybersecurity',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance Services',
    title: 'Email Authentication a Headache?',
    description:
      "Our team configures SPF, DKIM, and DMARC correctly the first time, with ongoing monitoring and reporting.",
  },
  'dmarc-deployment-policy-enforcement': {
    primaryService: 'small-business-cybersecurity',
    primaryServiceName: 'Small Business Cybersecurity',
    title: 'Struggling with DMARC Enforcement?',
    description:
      "We guide organizations through DMARC deployment from monitor to reject, protecting your domain reputation.",
  },
  'spf-dkim-dmarc-email-spoofing-prevention': {
    primaryService: 'small-business-cybersecurity',
    primaryServiceName: 'Small Business Cybersecurity',
    title: 'Stop Email Spoofing Attacks',
    description:
      "Our email security service configures and monitors SPF, DKIM, and DMARC to protect against impersonation.",
  },
  'check-point-harmony-vs-proofpoint-google-workspace-email-security': {
    primaryService: 'small-business-cybersecurity',
    primaryServiceName: 'Small Business Cybersecurity',
    title: 'Upgrade Your Email Security',
    description:
      "Our team evaluates, deploys, and manages modern email security solutions for Microsoft 365 and Google Workspace.",
  },
  'business-email-compromise-detection-headers': {
    primaryService: 'small-business-cybersecurity',
    primaryServiceName: 'Small Business Cybersecurity',
    secondaryService: '24-7-detection-and-response',
    secondaryServiceName: '24/7 Detection & Response',
    title: 'Worried About BEC Attacks?',
    description:
      "Business email compromise causes $2.7B in losses annually. Our email security stops BEC before it reaches inboxes.",
  },
  'email-header-analysis-forensics-guide': {
    primaryService: 'small-business-cybersecurity',
    primaryServiceName: 'Small Business Cybersecurity',
    secondaryService: 'incident-response',
    secondaryServiceName: 'Incident Response',
    title: 'Need Email Forensics?',
    description:
      "Our security team performs detailed email header analysis to trace attack origins and strengthen defenses.",
  },
  'how-to-prevent-email-spoofing': {
    primaryService: 'small-business-cybersecurity',
    primaryServiceName: 'Small Business Cybersecurity',
    title: 'Protect Your Domain from Spoofing',
    description:
      "Our email security service implements comprehensive spoofing protection with SPF, DKIM, DMARC, and BIMI.",
  },
  'email-security-hardening-workflow': {
    primaryService: 'small-business-cybersecurity',
    primaryServiceName: 'Small Business Cybersecurity',
    title: 'Get a Complete Email Hardening Plan',
    description:
      "Our 13-week email hardening program transforms your email security from vulnerable to bulletproof.",
  },
  'mta-sts-tls-rpt-email-encryption-guide': {
    primaryService: 'small-business-cybersecurity',
    primaryServiceName: 'Small Business Cybersecurity',
    title: 'Ensure Email Encryption in Transit',
    description:
      "Our team configures MTA-STS and TLS-RPT to enforce encrypted email delivery and monitor compliance.",
  },
  'why-your-employees-keep-clicking-phishing-emails-and-why-basic-filters-cant-stop-them': {
    primaryService: 'small-business-cybersecurity',
    primaryServiceName: 'Small Business Cybersecurity',
    title: 'Stop Advanced Phishing Attacks',
    description:
      "AI-powered phishing bypasses basic filters. Our email security service catches what others miss.",
  },
  'bimi-deliverability-brand-protection': {
    primaryService: 'small-business-cybersecurity',
    primaryServiceName: 'Small Business Cybersecurity',
    title: 'Boost Deliverability & Brand Trust',
    description:
      "BIMI displays your logo in inboxes and proves authenticity. We handle the technical implementation.",
  },
  'email-authentication-results-header-explained': {
    primaryService: 'small-business-cybersecurity',
    primaryServiceName: 'Small Business Cybersecurity',
    title: 'Decode Your Email Security',
    description:
      "Understanding authentication results is complex. Our team monitors and optimizes your email authentication.",
  },

  // ============================================
  // VULNERABILITY & PATCH MANAGEMENT BLOG POSTS
  // ============================================

  'how-do-i-prioritize-which-cves-to-patch-first': {
    primaryService: 'vulnerability-management',
    primaryServiceName: 'Vulnerability Management',
    secondaryService: 'vciso',
    secondaryServiceName: 'vCISO Services',
    title: 'Drowning in CVEs?',
    description:
      "Our vulnerability management service prioritizes patches based on exploitability, not just CVSS scores.",
  },
  'how-do-i-find-cves-affecting-my-software-and-systems': {
    primaryService: 'vulnerability-management',
    primaryServiceName: 'Vulnerability Management',
    title: 'Get Continuous Vulnerability Visibility',
    description:
      "Our scanning service identifies CVEs across your entire environment with prioritized remediation guidance.",
  },
  'beyond-wsus-how-to-build-a-modern-windows-update-management-system': {
    primaryService: 'managed-services',
    primaryServiceName: 'Managed IT Services',
    secondaryService: 'managed-services',
    secondaryServiceName: '24/7 IT Monitoring',
    title: 'Migrate from WSUS Painlessly',
    description:
      "Our team modernizes Windows patching with Intune, Azure Update Manager, and automated compliance reporting.",
  },
  'cyber-threats-cloud-trends-and-the-latest-cves-you-need-to-know': {
    primaryService: 'vulnerability-management',
    primaryServiceName: 'Vulnerability Management',
    secondaryService: '24-7-detection-and-response',
    secondaryServiceName: '24/7 Detection & Response',
    title: 'Stay Ahead of Emerging Threats',
    description:
      "Our security team monitors threat intelligence and ensures your defenses adapt to the latest CVEs.",
  },
  'how-long-does-it-take-for-a-cve-to-get-a-patch': {
    primaryService: 'vulnerability-management',
    primaryServiceName: 'Vulnerability Management',
    title: 'Bridge the Patching Gap',
    description:
      "Vendors take weeks to release patches. Our team implements compensating controls while you wait.",
  },

  // ============================================
  // CLOUD SECURITY BLOG POSTS
  // ============================================

  'cloud-security-assessment-complete-guide': {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    title: 'Get a Professional Cloud Security Review',
    description:
      "Our comprehensive assessment identifies misconfigurations, compliance gaps, and cost optimization opportunities.",
  },
  'cloud-security-assessment-aws-azure-gcp-comparison': {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    title: 'Multi-Cloud Security Experts',
    description:
      "Our team assesses and secures workloads across AWS, Azure, and GCP with unified visibility.",
  },
  'cloud-compliance-governance-validation': {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance Services',
    title: 'Validate Cloud Compliance',
    description:
      "Our assessment maps your cloud environment to SOC 2, HIPAA, PCI-DSS, and ISO 27001 requirements.",
  },
  'aws-s3-security-best-practices': {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    title: 'Secure Your S3 Buckets',
    description:
      "S3 misconfigurations cause data breaches. Our assessment identifies and remediates bucket security issues.",
  },
  'inventivehq-cloud-security-assessment-methodology': {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    title: 'See Our Assessment in Action',
    description:
      "Ready for a thorough cloud security review? Our methodology covers IAM, networking, encryption, and compliance.",
  },
  'how-often-should-i-conduct-cloud-security-assessments': {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    title: 'Schedule Your Cloud Assessment',
    description:
      "Cloud environments change constantly. Get continuous security visibility with our assessment service.",
  },

  // ============================================
  // COMPLIANCE BLOG POSTS
  // ============================================

  'compliance-gap-analysis-framework-selection': {
    primaryService: 'compliance',
    primaryServiceName: 'Compliance Services',
    secondaryService: 'vciso',
    secondaryServiceName: 'vCISO Services',
    title: 'Which Framework Is Right for You?',
    description:
      "Our compliance experts help you choose and implement the right frameworks for your business and customers.",
  },
  'multi-framework-compliance-mapping-guide': {
    primaryService: 'compliance',
    primaryServiceName: 'Compliance Services',
    title: 'Simplify Multi-Framework Compliance',
    description:
      "Managing SOC 2, ISO 27001, and HIPAA together? Our unified control mapping reduces duplicate effort by 60%.",
  },
  'iso-27001-certification-guide': {
    primaryService: 'compliance',
    primaryServiceName: 'Compliance Services',
    title: 'Get ISO 27001 Certified',
    description:
      "Our team guides you through ISMS implementation and certification with templates, gap analysis, and audit prep.",
  },
  'the-compliance-headache-for-smbs-why-cybersecurity-regulations-feel-impossible': {
    primaryService: 'compliance',
    primaryServiceName: 'Compliance Services',
    secondaryService: 'vciso',
    secondaryServiceName: 'vCISO Services',
    title: 'Compliance Doesn\'t Have to Be Painful',
    description:
      "Our experts simplify compliance for SMBs, handling the technical implementation while you focus on business.",
  },
  'continuous-compliance-monitoring-guide': {
    primaryService: 'compliance',
    primaryServiceName: 'Compliance Services',
    title: 'Automate Compliance Monitoring',
    description:
      "Our continuous monitoring service provides real-time control validation and automated evidence collection.",
  },
  'soc2-readiness-audit-prep-workflow': {
    primaryService: 'compliance',
    primaryServiceName: 'Compliance Services',
    title: 'Get SOC 2 Audit-Ready',
    description:
      "Our proven SOC 2 readiness program prepares you for Type I and Type II audits with minimal business disruption.",
  },
  'soc2-compliance': {
    primaryService: 'compliance',
    primaryServiceName: 'Compliance Services',
    title: 'Achieve SOC 2 Compliance',
    description:
      "Our team handles SOC 2 implementation from policies to audit, so you can win enterprise deals faster.",
  },
  'hipaa-compliance': {
    primaryService: 'compliance',
    primaryServiceName: 'Compliance Services',
    title: 'Achieve HIPAA Compliance',
    description:
      "Our healthcare compliance experts handle HIPAA assessments, policies, and ongoing compliance monitoring.",
  },
  'pci-dss-compliance': {
    primaryService: 'compliance',
    primaryServiceName: 'Compliance Services',
    title: 'Achieve PCI DSS Compliance',
    description:
      "Our team guides you through PCI DSS 4.0 requirements, SAQ completion, and network segmentation.",
  },
  'hipaa-security-assessment-workflow': {
    primaryService: 'compliance',
    primaryServiceName: 'Compliance Services',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance & Risk Assessment',
    title: 'Get Your HIPAA Assessment',
    description:
      "Our Security Rule assessment identifies gaps and creates actionable remediation plans for covered entities.",
  },
  'pci-dss-compliance-validation-workflow': {
    primaryService: 'compliance',
    primaryServiceName: 'Compliance Services',
    title: 'Validate PCI Compliance',
    description:
      "Our team handles PCI DSS 4.0.1 validation, SAQ completion, and network segmentation requirements.",
  },
  'nist-800-88-media-sanitization-complete-guide': {
    primaryService: 'compliance',
    primaryServiceName: 'Compliance Services',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance & Risk Assessment',
    title: 'Ensure Proper Data Destruction',
    description:
      "Our team implements NIST 800-88 compliant sanitization processes to protect sensitive data during disposal.",
  },

  // ============================================
  // PENETRATION TESTING BLOG POSTS
  // ============================================

  'penetration-testing-service': {
    primaryService: 'penetration-testing',
    primaryServiceName: 'Penetration Testing',
    title: 'Find Vulnerabilities Before Attackers',
    description:
      "Our certified pentesters simulate real-world attacks to identify weaknesses in your defenses.",
  },
  'penetration-testing-methodology-workflow': {
    primaryService: 'penetration-testing',
    primaryServiceName: 'Penetration Testing',
    title: 'Professional Penetration Testing',
    description:
      "Our PTES-aligned methodology provides thorough testing with actionable remediation guidance.",
  },
  'cloud-penetration-testing': {
    primaryService: 'penetration-testing',
    primaryServiceName: 'Penetration Testing',
    secondaryService: 'cloud-infrastructure-management',
    secondaryServiceName: 'Cloud Infrastructure & Optimization',
    title: 'Test Your Cloud Security',
    description:
      "Our cloud pentesting service identifies misconfigurations and vulnerabilities across AWS, Azure, and GCP.",
  },
  'api-penetration-testing-guide': {
    primaryService: 'penetration-testing',
    primaryServiceName: 'Penetration Testing',
    secondaryService: 'devsecops',
    secondaryServiceName: 'DevSecOps Services',
    title: 'Secure Your APIs',
    description:
      "Our API pentest covers OWASP API Top 10 and identifies authentication, authorization, and injection flaws.",
  },

  // ============================================
  // INCIDENT RESPONSE BLOG POSTS
  // ============================================

  'incident-response-services-for-small-medium-businesses': {
    primaryService: 'incident-response',
    primaryServiceName: 'Incident Response',
    secondaryService: '24-7-detection-and-response',
    secondaryServiceName: '24/7 Detection & Response',
    title: 'Be Ready Before a Breach',
    description:
      "Our IR retainer ensures expert responders are on-call when you need them most.",
  },
  'incident-response-forensics-workflow': {
    primaryService: 'incident-response',
    primaryServiceName: 'Incident Response',
    title: 'Expert Incident Response',
    description:
      "Our NIST/SANS-aligned IR team provides rapid response, forensic analysis, and recovery support.",
  },
  'data-breach-response-notification-workflow': {
    primaryService: 'incident-response',
    primaryServiceName: 'Incident Response',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance Services',
    title: 'Breach Response Experts',
    description:
      "Our team handles breach investigation, regulatory notifications, and stakeholder communications.",
  },
  'breaches-happen-why-smbs-need-incident-response-plans': {
    primaryService: 'incident-response',
    primaryServiceName: 'Incident Response',
    secondaryService: 'vciso',
    secondaryServiceName: 'vCISO Services',
    title: 'Get an IR Plan Before You Need It',
    description:
      "Our team develops and tests incident response plans tailored to your business and regulatory requirements.",
  },
  'cloud-incident-response-guide': {
    primaryService: 'incident-response',
    primaryServiceName: 'Incident Response',
    secondaryService: 'cloud-infrastructure-management',
    secondaryServiceName: 'Cloud Infrastructure & Optimization',
    title: 'Cloud Incident Response Experts',
    description:
      "Our team responds to cloud security incidents across AWS, Azure, and GCP with forensic expertise.",
  },

  // ============================================
  // VCISO & SECURITY STRATEGY BLOG POSTS
  // ============================================

  'in-house-ciso-vs-vciso': {
    primaryService: 'vciso',
    primaryServiceName: 'vCISO Services',
    title: 'Get C-Level Security Leadership',
    description:
      "Our vCISO service provides strategic security guidance at a fraction of full-time CISO cost.",
  },
  'risk-assessment-frameworks-smb-guide': {
    primaryService: 'vciso',
    primaryServiceName: 'vCISO Services',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance & Risk Assessment',
    title: 'Strategic Risk Management for SMBs',
    description:
      "Our vCISO team helps you prioritize security investments based on real business risk.",
  },
  'how-does-this-align-with-nist-cybersecurity-framework': {
    primaryService: 'vciso',
    primaryServiceName: 'vCISO Services',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance Services',
    title: 'Align with NIST CSF',
    description:
      "Our vCISO team maps your security program to NIST CSF and identifies improvement opportunities.",
  },
  'cybersecurity-for-ceos': {
    primaryService: 'vciso',
    primaryServiceName: 'vCISO Services',
    title: 'Security Strategy for Executives',
    description:
      "Our vCISO service translates technical security into business terms for board-level conversations.",
  },
  'how-does-company-size-affect-cybersecurity-spending': {
    primaryService: 'vciso',
    primaryServiceName: 'vCISO Services',
    title: 'Optimize Your Security Budget',
    description:
      "Our vCISO team helps you benchmark spending and prioritize investments for maximum ROI.",
  },
  'no-one-is-too-small-for-a-cyberattack-the-real-cost-of-thinking-youre-safe': {
    primaryService: 'vciso',
    primaryServiceName: 'vCISO Services',
    secondaryService: '24-7-detection-and-response',
    secondaryServiceName: '24/7 Detection & Response',
    title: 'Don\'t Wait for a Breach',
    description:
      "SMBs are prime targets. Our team provides enterprise-grade protection scaled for your business.",
  },
  'scaling-smb-it-from-reactive-fixes-to-strategic-growth': {
    primaryService: 'vciso',
    primaryServiceName: 'vCISO Services',
    secondaryService: 'managed-services',
    secondaryServiceName: 'Managed Services',
    title: 'Scale IT Strategically',
    description:
      "Our team helps growing businesses build IT infrastructure that enables rather than limits growth.",
  },

  // ============================================
  // INFRASTRUCTURE & CERTIFICATE BLOG POSTS
  // ============================================

  'ssl-tls-certificate-automation-renewal': {
    primaryService: 'managed-services',
    primaryServiceName: '24/7 IT Monitoring',
    secondaryService: 'devops-consulting',
    secondaryServiceName: 'DevOps Consulting',
    title: 'Automate Certificate Management',
    description:
      "Our team implements automated certificate renewal with ACME/Certbot to prevent expiration outages.",
  },
  'pki-certificate-authority-setup-guide': {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance Services',
    title: 'Build Enterprise PKI',
    description:
      "Our security team designs and implements private PKI infrastructure with offline root CAs.",
  },
  'mtls-mutual-tls-authentication-guide': {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    secondaryService: 'devsecops',
    secondaryServiceName: 'DevSecOps Services',
    title: 'Implement mTLS Authentication',
    description:
      "Our team secures service-to-service communication with mutual TLS and certificate management.",
  },
  'dns-domain-security-complete-guide': {
    primaryService: 'managed-services',
    primaryServiceName: '24/7 IT Monitoring',
    secondaryService: 'small-business-cybersecurity',
    secondaryServiceName: 'Small Business Cybersecurity',
    title: 'Secure Your DNS Infrastructure',
    description:
      "Our team monitors and secures DNS with DNSSEC, certificate transparency, and domain protection.",
  },
  'how-long-does-dns-propagation-take': {
    primaryService: 'managed-services',
    primaryServiceName: '24/7 IT Monitoring',
    title: 'DNS Changes Causing Issues?',
    description:
      "Our team manages DNS changes with proper TTL planning and propagation monitoring.",
  },

  // ============================================
  // DEVOPS & SECRETS MANAGEMENT BLOG POSTS
  // ============================================

  'hashicorp-vault-complete-guide': {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    secondaryService: 'devsecops',
    secondaryServiceName: 'DevSecOps Services',
    title: 'Enterprise Secrets Management',
    description:
      "Our team deploys and manages HashiCorp Vault for secure secrets, certificates, and encryption.",
  },
  'hashicorp-vault-authentication-configuration-guide': {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    secondaryService: 'devsecops',
    secondaryServiceName: 'DevSecOps Services',
    title: 'Secure Vault Authentication',
    description:
      "Our team configures Vault auth methods including OIDC, LDAP, AppRole, and cloud IAM integration.",
  },
  'hashicorp-vault-rekey-unseal-complete-security-guide': {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    title: 'Vault Security Operations',
    description:
      "Our team handles Vault rekey, unseal, and disaster recovery operations with proper security controls.",
  },
  'jira-webhooks-guide': {
    primaryService: 'devops-consulting',
    primaryServiceName: 'DevOps Consulting',
    secondaryService: 'software-development',
    secondaryServiceName: 'Software Development',
    title: 'Automate Your Jira Workflows',
    description:
      "Our DevOps team builds custom integrations that connect Jira with your development and security tools.",
  },

  // ============================================
  // SECURITY TOOLS
  // ============================================

  // Password & Authentication Tools
  'password-strength-checker': {
    primaryService: 'vciso',
    primaryServiceName: 'vCISO Services',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance Services',
    title: 'Weak Passwords Across Your Org?',
    description:
      "Our vCISO team implements enterprise password policies, SSO, and MFA aligned with NIST 800-63 guidelines.",
  },
  'secure-password-generator': {
    primaryService: 'vciso',
    primaryServiceName: 'vCISO Services',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance Services',
    title: 'Password Policies a Mess?',
    description:
      "Our vCISO service implements enterprise credential management aligned with NIST guidelines.",
  },

  // Vulnerability & Threat Intelligence Tools
  'cve-lookup': {
    primaryService: 'vulnerability-management',
    primaryServiceName: 'Vulnerability Management',
    secondaryService: '24-7-detection-and-response',
    secondaryServiceName: '24/7 Detection & Response',
    title: 'Tracking Vulnerabilities Manually?',
    description:
      "Our vulnerability management service provides continuous scanning and prioritized remediation guidance.",
  },
  'cwe-lookup': {
    primaryService: 'devsecops',
    primaryServiceName: 'DevSecOps Services',
    secondaryService: 'penetration-testing',
    secondaryServiceName: 'Penetration Testing',
    title: 'Finding the Same Weaknesses Repeatedly?',
    description:
      "Our DevSecOps team embeds security into your SDLC with SAST, code reviews, and developer training.",
  },
  'cvss-calculator': {
    primaryService: 'vulnerability-management',
    primaryServiceName: 'Vulnerability Management',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance & Risk Assessment',
    title: 'Prioritizing Vulnerabilities Without Context?',
    description:
      "Our vulnerability management service provides risk-based prioritization tailored to your environment.",
  },
  'mitre-attack': {
    primaryService: '24-7-detection-and-response',
    primaryServiceName: '24/7 Detection & Response',
    secondaryService: 'penetration-testing',
    secondaryServiceName: 'Penetration Testing',
    title: 'Building Detection Coverage?',
    description:
      "Our MDR team maps detections to MITRE ATT&CK and continuously improves coverage against emerging threats.",
  },
  'ioc-extractor': {
    primaryService: '24-7-detection-and-response',
    primaryServiceName: '24/7 Detection & Response',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance & Risk Assessment',
    title: 'Threat Intel Overwhelming Your Team?',
    description:
      "Our SOC analysts correlate IOCs, enrich indicators, and hunt for threats across your environment 24/7.",
  },
  'threat-intel-aggregator': {
    primaryService: '24-7-detection-and-response',
    primaryServiceName: '24/7 Detection & Response',
    secondaryService: 'vulnerability-management',
    secondaryServiceName: 'Vulnerability Management',
    title: 'Too Many Feeds, Not Enough Time?',
    description:
      "Our threat intelligence team curates, prioritizes, and operationalizes intel relevant to your industry.",
  },
  'hash-lookup': {
    primaryService: '24-7-detection-and-response',
    primaryServiceName: '24/7 Detection & Response',
    secondaryService: 'penetration-testing',
    secondaryServiceName: 'Penetration Testing',
    title: 'Suspicious Files on Your Network?',
    description:
      "Our incident response team investigates malware, performs forensic analysis, and contains threats.",
  },
  'ip-risk-checker': {
    primaryService: '24-7-detection-and-response',
    primaryServiceName: '24/7 Detection & Response',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance & Risk Assessment',
    title: 'Suspicious Traffic in Your Logs?',
    description:
      "Our SOC team monitors network traffic 24/7, correlating IPs against threat intelligence feeds.",
  },

  // Certificate & Key Management Tools
  'x509-decoder': {
    primaryService: 'managed-services',
    primaryServiceName: '24/7 IT Monitoring',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance Services',
    title: 'Certificate Management Getting Complex?',
    description:
      "We monitor certificate lifecycles, automate renewals, and prevent costly expirations.",
  },
  'certificate-csr-generator': {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance Services',
    title: 'Managing Certificates Across Environments?',
    description:
      "Our team implements certificate automation, PKI best practices, and compliance-ready configurations.",
  },
  'certificate-chain-builder': {
    primaryService: 'managed-services',
    primaryServiceName: '24/7 IT Monitoring',
    secondaryService: 'cloud-infrastructure-management',
    secondaryServiceName: 'Cloud Infrastructure & Optimization',
    title: 'SSL Chain Issues Breaking Your Site?',
    description:
      "We monitor and manage SSL certificates across your infrastructure to prevent outages.",
  },
  'ssl-checker': {
    primaryService: 'managed-services',
    primaryServiceName: '24/7 IT Monitoring',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance Services',
    title: 'Certificate Errors Hurting SEO?',
    description:
      "We manage SSL lifecycles across your infrastructure, preventing expiration and configuration issues.",
  },
  'ocsp-crl-checker': {
    primaryService: 'managed-services',
    primaryServiceName: '24/7 IT Monitoring',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance Services',
    title: 'Certificate Revocation Checks Failing?',
    description:
      "Our team monitors certificate status, configures OCSP stapling, and ensures compliance with PKI requirements.",
  },
  'ssh-key-generator': {
    primaryService: 'vciso',
    primaryServiceName: 'vCISO Services',
    secondaryService: 'cloud-infrastructure-management',
    secondaryServiceName: 'Cloud Infrastructure & Optimization',
    title: 'SSH Keys Scattered Everywhere?',
    description:
      "Our vCISO team implements centralized key management, rotation policies, and access controls.",
  },
  'pgp-key-generator': {
    primaryService: 'small-business-cybersecurity',
    primaryServiceName: 'Small Business Cybersecurity',
    secondaryService: 'cloud-infrastructure-management',
    secondaryServiceName: 'Cloud Infrastructure & Optimization',
    title: 'Secure Your Communications',
    description:
      "Our email security service protects against phishing, BEC, and data exfiltration.",
  },
  'certificate-transparency-lookup': {
    primaryService: 'compliance',
    primaryServiceName: 'Compliance & Risk Assessment',
    secondaryService: 'vciso',
    secondaryServiceName: 'vCISO Services',
    title: 'Detecting Phishing Domains?',
    description:
      "CT logs reveal fraudulent certificates. Our team monitors for brand impersonation and domain abuse.",
  },

  // Encryption & Cryptography Tools
  'xor-cipher': {
    primaryService: 'penetration-testing',
    primaryServiceName: 'Penetration Testing',
    secondaryService: 'cloud-infrastructure-management',
    secondaryServiceName: 'Cloud Infrastructure & Optimization',
    title: 'Security Testing & Assessment',
    description:
      "Our penetration testers identify cryptographic weaknesses and vulnerabilities in your applications.",
  },
  'aes-encryption-tool': {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance Services',
    title: 'Encryption Implementation Questions?',
    description:
      "Our security team reviews encryption implementations, key management, and data protection controls.",
  },
  'hash-generator': {
    primaryService: 'devsecops',
    primaryServiceName: 'DevSecOps Services',
    secondaryService: 'cloud-infrastructure-management',
    secondaryServiceName: 'Cloud Infrastructure & Optimization',
    title: 'Securing Your Application Data?',
    description:
      "Our DevSecOps team implements proper hashing, encryption, and data protection in your applications.",
  },
  'base64-encoder-decoder': {
    primaryService: 'software-development',
    primaryServiceName: 'Software Development',
    secondaryService: 'devsecops',
    secondaryServiceName: 'DevSecOps Services',
    title: 'Building Secure Data Handling?',
    description:
      "Our development team implements secure encoding, encryption, and data transformation patterns.",
  },
  'rot13-cipher': {
    primaryService: 'penetration-testing',
    primaryServiceName: 'Penetration Testing',
    secondaryService: 'devsecops',
    secondaryServiceName: 'DevSecOps Services',
    title: 'Learning Security Fundamentals?',
    description:
      "Our security team provides training, assessments, and guidance for building secure applications.",
  },

  // Classical Ciphers (Educational/CTF)
  'caesar-cipher': {
    primaryService: 'penetration-testing',
    primaryServiceName: 'Penetration Testing',
    secondaryService: 'devsecops',
    secondaryServiceName: 'DevSecOps Services',
    title: 'Building Security Skills?',
    description:
      "From CTF competitions to professional pentesting, our team helps you level up your security expertise.",
  },
  'vigenere-cipher': {
    primaryService: 'penetration-testing',
    primaryServiceName: 'Penetration Testing',
    secondaryService: 'cloud-infrastructure-management',
    secondaryServiceName: 'Cloud Infrastructure & Optimization',
    title: 'Interested in Cryptanalysis?',
    description:
      "Our security team performs deep cryptographic analysis and testing for your applications.",
  },
  'substitution-cipher': {
    primaryService: 'penetration-testing',
    primaryServiceName: 'Penetration Testing',
    title: 'Solving Security Puzzles?',
    description:
      "Our penetration testers love a challenge. Let us find the vulnerabilities in your systems.",
  },
  'cipher-suite': {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance Services',
    title: 'TLS Configuration Concerns?',
    description:
      "Our team audits cipher suites, TLS configurations, and cryptographic implementations for compliance.",
  },
  'cipher-identifier': {
    primaryService: 'penetration-testing',
    primaryServiceName: 'Penetration Testing',
    secondaryService: '24-7-detection-and-response',
    secondaryServiceName: '24/7 Detection & Response',
    title: 'Analyzing Unknown Encryption?',
    description:
      "Our security analysts investigate suspicious encoded content and malware communications.",
  },
  'affine-cipher': {
    primaryService: 'penetration-testing',
    primaryServiceName: 'Penetration Testing',
    title: 'Preparing for Security Certifications?',
    description:
      "Our team provides security training, mentorship, and hands-on labs for aspiring professionals.",
  },
  'atbash-cipher': {
    primaryService: 'penetration-testing',
    primaryServiceName: 'Penetration Testing',
    title: 'Learning Cryptography Fundamentals?',
    description:
      "Understanding historical ciphers builds intuition for modern security. Let us help you grow.",
  },
  'rail-fence-cipher': {
    primaryService: 'penetration-testing',
    primaryServiceName: 'Penetration Testing',
    title: 'Exploring Transposition Ciphers?',
    description:
      "Our security team understands both classical and modern cryptographic techniques.",
  },
  'playfair-cipher': {
    primaryService: 'penetration-testing',
    primaryServiceName: 'Penetration Testing',
    title: 'Studying Historical Cryptography?',
    description:
      "From Playfair to AES-256, our team ensures your encryption implementations are bulletproof.",
  },
  'beaufort-cipher': {
    primaryService: 'penetration-testing',
    primaryServiceName: 'Penetration Testing',
    title: 'Deep Diving Into Ciphers?',
    description:
      "Our security researchers stay current with cryptographic advances and implementation best practices.",
  },
  'hill-cipher': {
    primaryService: 'penetration-testing',
    primaryServiceName: 'Penetration Testing',
    secondaryService: 'devsecops',
    secondaryServiceName: 'DevSecOps Services',
    title: 'Implementing Matrix Cryptography?',
    description:
      "Our team reviews cryptographic implementations and ensures they meet security standards.",
  },
  'enigma-simulator': {
    primaryService: 'penetration-testing',
    primaryServiceName: 'Penetration Testing',
    title: 'Fascinated by Crypto History?',
    description:
      "From WWII Enigma to quantum cryptography, our team stays ahead of the security curve.",
  },
  'one-time-pad': {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    secondaryService: 'vciso',
    secondaryServiceName: 'vCISO Services',
    title: 'Need Perfect Secrecy for Sensitive Data?',
    description:
      "Our security architects design encryption strategies for your most sensitive information.",
  },
  'rsa-playground': {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    secondaryService: 'devsecops',
    secondaryServiceName: 'DevSecOps Services',
    title: 'Implementing Public Key Crypto?',
    description:
      "Our team reviews RSA implementations, key sizes, and cryptographic configurations.",
  },
  'public-key-concepts': {
    primaryService: 'vciso',
    primaryServiceName: 'vCISO Services',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance Services',
    title: 'Training Your Team on Security?',
    description:
      "Our vCISO service includes security awareness training tailored to your organization.",
  },
  'encoding-chain-analyzer': {
    primaryService: 'penetration-testing',
    primaryServiceName: 'Penetration Testing',
    secondaryService: '24-7-detection-and-response',
    secondaryServiceName: '24/7 Detection & Response',
    title: 'Investigating Obfuscated Payloads?',
    description:
      "Our incident response team decodes malicious payloads and traces attack chains.",
  },
  'crypto-mode-visualizer': {
    primaryService: 'devsecops',
    primaryServiceName: 'DevSecOps Services',
    secondaryService: 'cloud-infrastructure-management',
    secondaryServiceName: 'Cloud Infrastructure & Optimization',
    title: 'Choosing the Right Encryption Mode?',
    description:
      "Our DevSecOps team ensures your applications use proper encryption modes and implementations.",
  },

  // JWT & Authentication Tools
  'jwt-decoder': {
    primaryService: 'devsecops',
    primaryServiceName: 'DevSecOps Services',
    secondaryService: 'penetration-testing',
    secondaryServiceName: 'Penetration Testing',
    title: 'JWT Security Concerns?',
    description:
      "Our DevSecOps team audits authentication implementations, token handling, and API security.",
  },
  'oauth-oidc-debugger': {
    primaryService: 'devsecops',
    primaryServiceName: 'DevSecOps Services',
    secondaryService: 'cloud-infrastructure-management',
    secondaryServiceName: 'Cloud Infrastructure & Optimization',
    title: 'OAuth/OIDC Flows Not Working?',
    description:
      "Our team implements secure authentication flows, SSO integrations, and identity federation.",
  },
  'federated-identity-architect': {
    primaryService: 'vciso',
    primaryServiceName: 'vCISO Services',
    secondaryService: 'cloud-infrastructure-management',
    secondaryServiceName: 'Cloud Infrastructure & Optimization',
    title: 'Designing Identity Architecture?',
    description:
      "Our vCISO team designs enterprise identity solutions with SSO, federation, and zero-trust principles.",
  },
  'biometric-performance-simulator': {
    primaryService: 'vciso',
    primaryServiceName: 'vCISO Services',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance Services',
    title: 'Implementing Biometric Authentication?',
    description:
      "Our security architects design multi-factor authentication systems that balance security and usability.",
  },

  // Email Security Tools
  'phishing-email-analyzer': {
    primaryService: 'email-security',
    primaryServiceName: 'Managed Email Security',
    secondaryService: '24-7-detection-and-response',
    secondaryServiceName: '24/7 Detection & Response',
    title: 'Stop Analyzing Phishing One-by-One',
    description:
      "Inspecting suspicious emails by hand doesn't scale. Our managed email security blocks phishing and BEC before it ever reaches the inbox — powered by Check Point Harmony.",
  },
  'email-auth-validator': {
    primaryService: 'email-security',
    primaryServiceName: 'Managed Email Security',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance Services',
    title: 'Emails Landing in Spam?',
    description:
      "Misconfigured SPF/DKIM/DMARC hurts deliverability and leaves you open to spoofing. Our managed email security configures and monitors authentication so good mail lands and bad mail doesn't.",
  },
  'email-validator-mx-checker': {
    primaryService: 'email-security',
    primaryServiceName: 'Managed Email Security',
    secondaryService: 'managed-services',
    secondaryServiceName: '24/7 IT Monitoring',
    title: 'Lock Down Your Mail Infrastructure',
    description:
      "MX and mail-server checks are step one. Our managed email security hardens and continuously monitors your mail flow against phishing, BEC, and outages.",
  },
  'email-header-analyzer': {
    primaryService: 'email-security',
    primaryServiceName: 'Managed Email Security',
    secondaryService: '24-7-detection-and-response',
    secondaryServiceName: '24/7 Detection & Response',
    title: 'Investigating Phishing Attacks?',
    description:
      "Header forensics catch threats after they arrive. Our managed email security stops them at the gateway — analyzing, tracing, and blocking attacks before users see them.",
  },
  'dmarc-generator': {
    primaryService: 'email-security',
    primaryServiceName: 'Managed Email Security',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance Services',
    title: 'DMARC Deployment Overwhelming?',
    description:
      "Generating a record is easy; safely reaching p=reject is the hard part. Our managed email security deploys DMARC with monitoring, gradual enforcement, and report analysis.",
  },
  'spf-generator': {
    primaryService: 'email-security',
    primaryServiceName: 'Managed Email Security',
    secondaryService: 'managed-services',
    secondaryServiceName: '24/7 IT Monitoring',
    title: 'SPF Record Too Complex?',
    description:
      "SPF lookups, includes, and the 10-DNS-lookup limit get messy fast. Our managed email security optimizes SPF and ties it into full anti-spoofing protection.",
  },
  'email-setup-wizard': {
    primaryService: 'small-business-cybersecurity',
    primaryServiceName: 'Small Business Cybersecurity',
    secondaryService: 'productivity-suite-management',
    secondaryServiceName: 'Productivity Suite Management',
    title: 'Setting Up Email for a New Domain?',
    description:
      "We configure email authentication, security, and deliverability from day one.",
  },
  'email-template-generator': {
    primaryService: 'vciso',
    primaryServiceName: 'vCISO Services',
    secondaryService: '24-7-detection-and-response',
    secondaryServiceName: '24/7 Detection & Response',
    title: 'Building Incident Communication Plans?',
    description:
      "Our vCISO team develops incident response playbooks with tested communication templates.",
  },

  // Security Headers & Web Security Tools
  'security-headers-analyzer': {
    primaryService: 'penetration-testing',
    primaryServiceName: 'Penetration Testing',
    secondaryService: 'devsecops',
    secondaryServiceName: 'DevSecOps Services',
    title: 'Security Headers Missing?',
    description:
      "Missing headers expose you to clickjacking and XSS. Our security team hardens your web applications.",
  },
  'csp-generator': {
    primaryService: 'devsecops',
    primaryServiceName: 'DevSecOps Services',
    secondaryService: 'penetration-testing',
    secondaryServiceName: 'Penetration Testing',
    title: 'CSP Breaking Your Site?',
    description:
      "Our DevSecOps team implements Content Security Policies that protect without disrupting functionality.",
  },
  'cors-policy-analyzer': {
    primaryService: 'devsecops',
    primaryServiceName: 'DevSecOps Services',
    secondaryService: 'penetration-testing',
    secondaryServiceName: 'Penetration Testing',
    title: 'CORS Misconfigurations Exposing Data?',
    description:
      "Our security team audits API security, CORS policies, and cross-origin vulnerabilities.",
  },
  'robots-txt-analyzer': {
    primaryService: 'penetration-testing',
    primaryServiceName: 'Penetration Testing',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance & Risk Assessment',
    title: 'Exposing Sensitive Paths to Crawlers?',
    description:
      "Our penetration testers find information disclosure vulnerabilities and configuration issues.",
  },
  'cookie-analyzer': {
    primaryService: 'compliance',
    primaryServiceName: 'Compliance Services',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance & Risk Assessment',
    title: 'GDPR/CCPA Compliance Concerns?',
    description:
      "Tracking cookies without consent can lead to fines. We audit and remediate cookie compliance issues.",
  },

  // Malware Analysis & Forensics Tools
  'malware-deobfuscator': {
    primaryService: '24-7-detection-and-response',
    primaryServiceName: '24/7 Detection & Response',
    secondaryService: 'penetration-testing',
    secondaryServiceName: 'Penetration Testing',
    title: 'Analyzing Suspicious Code?',
    description:
      "Our malware analysts reverse engineer threats, develop signatures, and improve defenses.",
  },
  'url-defanger': {
    primaryService: '24-7-detection-and-response',
    primaryServiceName: '24/7 Detection & Response',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance & Risk Assessment',
    title: 'Sharing Threat Intel Safely?',
    description:
      "Our SOC team documents and shares IOCs while maintaining operational security.",
  },
  'string-extractor': {
    primaryService: '24-7-detection-and-response',
    primaryServiceName: '24/7 Detection & Response',
    secondaryService: 'penetration-testing',
    secondaryServiceName: 'Penetration Testing',
    title: 'Reverse Engineering Malware?',
    description:
      "Our incident response team performs deep malware analysis and develops countermeasures.",
  },
  'entropy-analyzer': {
    primaryService: '24-7-detection-and-response',
    primaryServiceName: '24/7 Detection & Response',
    secondaryService: 'penetration-testing',
    secondaryServiceName: 'Penetration Testing',
    title: 'Detecting Packed Malware?',
    description:
      "Our analysts use advanced techniques to identify and analyze obfuscated threats.",
  },
  'hex-editor': {
    primaryService: 'penetration-testing',
    primaryServiceName: 'Penetration Testing',
    title: 'Professional Security Analysis',
    description:
      "Our security team performs deep analysis and testing to find vulnerabilities before attackers.",
  },
  'file-magic-number-checker': {
    primaryService: '24-7-detection-and-response',
    primaryServiceName: '24/7 Detection & Response',
    secondaryService: 'penetration-testing',
    secondaryServiceName: 'Penetration Testing',
    title: 'Verifying File Types in Uploads?',
    description:
      "Our security team implements file validation, malware scanning, and secure upload handling.",
  },
  'machine-code-disassembler': {
    primaryService: 'penetration-testing',
    primaryServiceName: 'Penetration Testing',
    secondaryService: '24-7-detection-and-response',
    secondaryServiceName: '24/7 Detection & Response',
    title: 'Analyzing Compiled Binaries?',
    description:
      "Our reverse engineering team analyzes malware, firmware, and compiled applications.",
  },
  'webassembly-debugger': {
    primaryService: 'penetration-testing',
    primaryServiceName: 'Penetration Testing',
    secondaryService: 'application-security',
    secondaryServiceName: 'Application Security',
    title: 'Analyzing WebAssembly Security?',
    description:
      "Our application security team reviews WebAssembly modules, browser attack surfaces, and compiled application behavior.",
  },
  'binary-lab': {
    primaryService: 'penetration-testing',
    primaryServiceName: 'Penetration Testing',
    secondaryService: '24-7-detection-and-response',
    secondaryServiceName: '24/7 Detection & Response',
    title: 'Need a Deeper Binary Investigation?',
    description: 'Our security team performs controlled reverse engineering, malware triage, and evidence-driven incident analysis.',
  },
  'executable-file-inspector': {
    primaryService: 'penetration-testing',
    primaryServiceName: 'Penetration Testing',
    secondaryService: 'application-security',
    secondaryServiceName: 'Application Security',
    title: 'Reviewing an Untrusted Executable?',
    description: 'Our reverse engineering team analyzes executable structure, mitigations, dependencies, signatures, and suspicious behavior.',
  },
  'assembler-playground': {
    primaryService: 'application-security',
    primaryServiceName: 'Application Security',
    secondaryService: 'penetration-testing',
    secondaryServiceName: 'Penetration Testing',
    title: 'Testing Low-Level Application Security?',
    description: 'Our application security team reviews native code, unsafe interfaces, and exploit-relevant implementation details.',
  },
  'yara-rule-workbench': {
    primaryService: '24-7-detection-and-response',
    primaryServiceName: '24/7 Detection & Response',
    secondaryService: 'penetration-testing',
    secondaryServiceName: 'Penetration Testing',
    title: 'Need Production Detection Engineering?',
    description: 'Our detection engineers develop, validate, tune, and operationalize threat detections across your security stack.',
  },
  'binary-diff-patch': {
    primaryService: 'application-security',
    primaryServiceName: 'Application Security',
    secondaryService: 'penetration-testing',
    secondaryServiceName: 'Penetration Testing',
    title: 'Validating a Binary Release?',
    description: 'Our application security team reviews release artifacts, software changes, update paths, and supply-chain controls.',
  },
  'shellcode-emulator': {
    primaryService: 'penetration-testing',
    primaryServiceName: 'Penetration Testing',
    secondaryService: '24-7-detection-and-response',
    secondaryServiceName: '24/7 Detection & Response',
    title: 'Need Controlled Exploit Analysis?',
    description: 'Our specialists analyze suspicious payloads and exploit behavior in hardened, isolated environments.',
  },
  'network-investigation-lab': {
    primaryService: 'managed-services',
    primaryServiceName: 'Managed IT Services',
    secondaryService: '24-7-detection-and-response',
    secondaryServiceName: '24/7 Detection & Response',
    title: 'Need a Deeper Network Investigation?',
    description: 'Our team investigates network behavior, validates exposure, and turns findings into monitored remediation.',
  },
  'identity-protocol-lab': {
    primaryService: 'application-security',
    primaryServiceName: 'Application Security',
    secondaryService: 'penetration-testing',
    secondaryServiceName: 'Penetration Testing',
    title: 'Reviewing an Identity Integration?',
    description: 'Our application security team tests authentication flows, federation boundaries, tokens, and authorization controls.',
  },
  'certificate-trust-lab': {
    primaryService: 'application-security',
    primaryServiceName: 'Application Security',
    secondaryService: 'managed-services',
    secondaryServiceName: 'Managed IT Services',
    title: 'Need Certificate Lifecycle Help?',
    description: 'Our team reviews certificate trust, deployment, renewal, key handling, and TLS configuration across your environment.',
  },
  'reliability-engineering-lab': {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    secondaryService: 'devops-consulting',
    secondaryServiceName: 'DevOps Consulting',
    title: 'Ready to Engineer Reliability?',
    description: 'We turn SLOs, recovery requirements, dependencies, and capacity signals into resilient production architecture.',
  },
  'security-program-workspace': {
    primaryService: 'vciso',
    primaryServiceName: 'vCISO Services',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance & Risk Assessment',
    title: 'Need a Security Program That Moves?',
    description: 'Our vCISO team builds accountable roadmaps, closes priority control gaps, and prepares evidence for formal review.',
  },
  'local-ai-deployment-lab': {
    primaryService: 'ai-security-audit',
    primaryServiceName: 'AI Security Audit',
    secondaryService: 'cloud-infrastructure-management',
    secondaryServiceName: 'Cloud Infrastructure & Optimization',
    title: 'Planning a Private AI Deployment?',
    description: 'We design secure AI infrastructure, validate model and data controls, and right-size production deployment paths.',
  },

  // Penetration Testing Tools
  'nmap-command-builder': {
    primaryService: 'penetration-testing',
    primaryServiceName: 'Penetration Testing',
    secondaryService: 'vulnerability-management',
    secondaryServiceName: 'Vulnerability Management',
    title: 'Planning a Network Assessment?',
    description:
      "Our penetration testers perform comprehensive network assessments with detailed reporting.",
  },
  'metasploit-command-builder': {
    primaryService: 'penetration-testing',
    primaryServiceName: 'Penetration Testing',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance & Risk Assessment',
    title: 'Need Professional Exploitation Testing?',
    description:
      "Our certified penetration testers safely validate vulnerabilities without disrupting operations.",
  },
  'domain-spoofing-detector': {
    primaryService: 'email-security',
    primaryServiceName: 'Managed Email Security',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance & Risk Assessment',
    title: 'Worried About Brand Impersonation?',
    description:
      "Detecting a spoof is one thing; stopping it is another. Our managed email security enforces SPF/DKIM/DMARC and blocks lookalike-domain phishing before it reaches your users.",
  },

  // Security Architecture & Policy Tools
  'incident-response-playbook-generator': {
    primaryService: 'vciso',
    primaryServiceName: 'vCISO Services',
    secondaryService: '24-7-detection-and-response',
    secondaryServiceName: '24/7 Detection & Response',
    title: 'Building an IR Program?',
    description:
      "Our vCISO team develops complete incident response programs with tested playbooks and tabletop exercises.",
  },
  'nist-csf-mapper': {
    primaryService: 'compliance',
    primaryServiceName: 'Compliance Services',
    secondaryService: 'vciso',
    secondaryServiceName: 'vCISO Services',
    title: 'Mapping Controls Across Frameworks?',
    description:
      "Our compliance team harmonizes requirements across NIST, ISO, SOC 2, and other frameworks.",
  },
  'siem-query-builder': {
    primaryService: '24-7-detection-and-response',
    primaryServiceName: '24/7 Detection & Response',
    secondaryService: 'managed-services',
    secondaryServiceName: '24/7 IT Monitoring',
    title: 'SIEM Detection Gaps?',
    description:
      "Our SOC engineers build custom detection rules and tune your SIEM for maximum visibility.",
  },
  'threat-modeling-wizard': {
    primaryService: 'devsecops',
    primaryServiceName: 'DevSecOps Services',
    secondaryService: 'vciso',
    secondaryServiceName: 'vCISO Services',
    title: 'Starting Threat Modeling?',
    description:
      "Our security architects conduct threat modeling workshops and embed security into your SDLC.",
  },
  'security-model-decision-matrix': {
    primaryService: 'vciso',
    primaryServiceName: 'vCISO Services',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance Services',
    title: 'Designing Security Architecture?',
    description:
      "Our vCISO team designs security architectures aligned with your regulatory and business requirements.",
  },
  'media-sanitization-advisor': {
    primaryService: 'compliance',
    primaryServiceName: 'Compliance Services',
    secondaryService: 'vciso',
    secondaryServiceName: 'vCISO Services',
    title: 'Disposing of Sensitive Media?',
    description:
      "Our compliance team implements NIST 800-88 compliant sanitization and destruction procedures.",
  },
  'database-inference-simulator': {
    primaryService: 'devsecops',
    primaryServiceName: 'DevSecOps Services',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance Services',
    title: 'Protecting Sensitive Database Data?',
    description:
      "Our team implements database security controls, access logging, and inference prevention.",
  },
  'systemlens': {
    primaryService: 'vulnerability-management',
    primaryServiceName: 'Vulnerability Management',
    secondaryService: '24-7-detection-and-response',
    secondaryServiceName: '24/7 Detection & Response',
    title: 'Auditing System Security?',
    description:
      "Our team performs comprehensive security assessments across your infrastructure.",
  },

  // ============================================
  // NETWORK TOOLS
  // ============================================

  'dns-lookup': {
    primaryService: 'email-security',
    primaryServiceName: 'Managed Email Security',
    secondaryService: 'managed-services',
    secondaryServiceName: '24/7 IT Monitoring',
    title: 'Checking SPF, DKIM & DMARC Records?',
    description:
      "DNS is where email authentication lives — and where spoofing slips through. Our managed email security configures and monitors SPF/DKIM/DMARC so your domain can't be impersonated.",
  },
  'subnet-calculator': {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    secondaryService: 'vciso',
    secondaryServiceName: 'vCISO Services',
    title: 'Planning a Network Migration?',
    description:
      "Our architects design scalable, secure network topologies for cloud and on-premise environments.",
  },
  'port-reference': {
    primaryService: 'managed-services',
    primaryServiceName: '24/7 IT Monitoring',
    secondaryService: 'penetration-testing',
    secondaryServiceName: 'Penetration Testing',
    title: 'Firewall Rules Causing Issues?',
    description:
      "Our team audits firewall configurations, optimizes rules, and ensures secure network segmentation.",
  },
  'url-expander': {
    primaryService: 'small-business-cybersecurity',
    primaryServiceName: 'Small Business Cybersecurity',
    secondaryService: '24-7-detection-and-response',
    secondaryServiceName: '24/7 Detection & Response',
    title: 'Suspicious Links in Emails?',
    description:
      "Our email security service analyzes links, blocks phishing, and protects your users.",
  },
  'redirect-chain-checker': {
    primaryService: 'penetration-testing',
    primaryServiceName: 'Penetration Testing',
    secondaryService: 'managed-services',
    secondaryServiceName: '24/7 IT Monitoring',
    title: 'Open Redirects in Your App?',
    description:
      "Our security team identifies and remediates redirect vulnerabilities and misconfigurations.",
  },
  'whois-lookup': {
    primaryService: 'compliance',
    primaryServiceName: 'Compliance & Risk Assessment',
    secondaryService: 'penetration-testing',
    secondaryServiceName: 'Penetration Testing',
    title: 'Investigating Suspicious Domains?',
    description:
      "Our security team performs threat intelligence and domain analysis as part of comprehensive risk assessments.",
  },
  'ip-geolocation-lookup': {
    primaryService: 'managed-services',
    primaryServiceName: '24/7 IT Monitoring',
    secondaryService: 'cloud-infrastructure-management',
    secondaryServiceName: 'Cloud Infrastructure & Optimization',
    title: 'Tracking User Access Patterns?',
    description:
      "We provide 24/7 monitoring and analysis of access patterns to detect anomalies and optimize performance.",
  },
  'bgp-as-lookup': {
    primaryService: 'managed-services',
    primaryServiceName: '24/7 IT Monitoring',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance & Risk Assessment',
    title: 'Investigating Network Routes?',
    description:
      "Our team monitors BGP routing, detects hijacks, and ensures network connectivity.",
  },
  'mac-address-lookup': {
    primaryService: 'managed-services',
    primaryServiceName: '24/7 IT Monitoring',
    secondaryService: '24-7-detection-and-response',
    secondaryServiceName: '24/7 Detection & Response',
    title: 'Unknown Devices on Your Network?',
    description:
      "Our team implements network access control, device monitoring, and rogue device detection.",
  },
  'network-latency-calculator': {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    secondaryService: 'managed-services',
    secondaryServiceName: '24/7 IT Monitoring',
    title: 'Optimize Your Network Performance',
    description:
      "Our cloud and infrastructure experts can identify and resolve latency issues across your environment.",
  },
  'tcp-window-size-calculator': {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    secondaryService: 'managed-services',
    secondaryServiceName: '24/7 IT Monitoring',
    title: 'WAN Performance Issues?',
    description:
      "Our network engineers optimize TCP settings, tune buffers, and maximize throughput.",
  },
  'wireless-security-planner': {
    primaryService: 'vciso',
    primaryServiceName: 'vCISO Services',
    secondaryService: 'penetration-testing',
    secondaryServiceName: 'Penetration Testing',
    title: 'Securing Your Wireless Network?',
    description:
      "Our team designs secure wireless architectures with proper encryption, segmentation, and monitoring.",
  },
  'ipv4-ipv6-converter': {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    secondaryService: 'managed-services',
    secondaryServiceName: '24/7 IT Monitoring',
    title: 'Planning IPv6 Migration?',
    description:
      "Our network architects design dual-stack implementations and IPv6 transition strategies.",
  },
  'firewall-rule-simulator': {
    primaryService: 'penetration-testing',
    primaryServiceName: 'Penetration Testing',
    secondaryService: 'vciso',
    secondaryServiceName: 'vCISO Services',
    title: 'Firewall Rules Getting Complex?',
    description:
      "Our security team audits firewall configurations, optimizes rulesets, and implements microsegmentation.",
  },

  // ============================================
  // DEVELOPER TOOLS
  // ============================================

  // DevOps & Container Tools
  'terraform-plan-explainer': {
    primaryService: 'devops-consulting',
    primaryServiceName: 'DevOps Consulting',
    secondaryService: 'devsecops',
    secondaryServiceName: 'DevSecOps Services',
    title: 'Infrastructure as Code Experts',
    description:
      "Our DevOps consultants design Terraform modules, state management, and CI/CD pipelines for safe infrastructure deployments.",
  },
  'docker-command-builder': {
    primaryService: 'devops-consulting',
    primaryServiceName: 'DevOps Consulting',
    secondaryService: 'devsecops',
    secondaryServiceName: 'DevSecOps Services',
    title: 'Container & DevOps Expertise',
    description:
      "From Docker to Kubernetes, our DevOps team containerizes applications and builds secure deployment pipelines.",
  },
  'dockerfile-generator': {
    primaryService: 'devops-consulting',
    primaryServiceName: 'DevOps Consulting',
    secondaryService: 'devsecops',
    secondaryServiceName: 'DevSecOps Services',
    title: 'Container Security & Best Practices',
    description:
      "Our DevOps consultants build optimized, secure container images and CI/CD pipelines for production workloads.",
  },
  'kubernetes-manifest-validator': {
    primaryService: 'devsecops',
    primaryServiceName: 'DevSecOps Services',
    secondaryService: 'devops-consulting',
    secondaryServiceName: 'DevOps Consulting',
    title: 'Need Expert Kubernetes Security?',
    description:
      "Our DevSecOps team secures Kubernetes clusters with admission controllers, CIS benchmarks, and runtime protection.",
  },
  'sbom-generator': {
    primaryService: 'devsecops',
    primaryServiceName: 'DevSecOps Services',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance Services',
    title: 'Software Supply Chain Security',
    description:
      "Our DevSecOps team implements SBOM generation, dependency scanning, and supply chain security for EO 14028 compliance.",
  },
  'secrets-scanner': {
    primaryService: 'devsecops',
    primaryServiceName: 'DevSecOps Services',
    secondaryService: 'devops-consulting',
    secondaryServiceName: 'DevOps Consulting',
    title: 'Secrets Management Experts',
    description:
      "Our DevSecOps team implements HashiCorp Vault, secrets scanning, and secure CI/CD pipelines.",
  },

  // API & Data Tools
  'http-request-builder': {
    primaryService: 'software-development',
    primaryServiceName: 'Software Development',
    secondaryService: 'devops-consulting',
    secondaryServiceName: 'DevOps Consulting',
    title: 'Building API Integrations?',
    description:
      "Our developers build robust API integrations with proper authentication and error handling.",
  },
  'json-formatter': {
    primaryService: 'devops-consulting',
    primaryServiceName: 'DevOps Consulting',
    secondaryService: 'software-development',
    secondaryServiceName: 'Software Development',
    title: 'Data Pipeline Headaches?',
    description:
      "Struggling with JSON parsing and API integrations? Our DevOps team automates data workflows.",
  },
  'json-validator': {
    primaryService: 'software-development',
    primaryServiceName: 'Software Development',
    secondaryService: 'devops-consulting',
    secondaryServiceName: 'DevOps Consulting',
    title: 'API Errors Driving You Crazy?',
    description:
      "Schema validation issues slow development. Our team builds robust APIs with proper error handling.",
  },
  'csv-json-converter': {
    primaryService: 'software-development',
    primaryServiceName: 'Software Development',
    secondaryService: 'devops-consulting',
    secondaryServiceName: 'DevOps Consulting',
    title: 'Data Transformation Challenges?',
    description:
      "Our developers build data pipelines, ETL processes, and format conversion tools.",
  },
  'excel-to-json-converter': {
    primaryService: 'software-development',
    primaryServiceName: 'Software Development',
    title: 'Automating Spreadsheet Processing?',
    description:
      "Our team builds automated data ingestion pipelines from Excel, CSV, and other formats.",
  },
  'yaml-json-converter': {
    primaryService: 'devops-consulting',
    primaryServiceName: 'DevOps Consulting',
    secondaryService: 'software-development',
    secondaryServiceName: 'Software Development',
    title: 'Managing Configuration Files?',
    description:
      "Our DevOps team implements configuration management, validation, and deployment automation.",
  },
  'data-format-converter': {
    primaryService: 'software-development',
    primaryServiceName: 'Software Development',
    secondaryService: 'devops-consulting',
    secondaryServiceName: 'DevOps Consulting',
    title: 'Complex Data Transformations?',
    description:
      "Our developers build reliable data transformation pipelines for any format.",
  },
  'webhook-tester-inspector': {
    primaryService: 'software-development',
    primaryServiceName: 'Software Development',
    secondaryService: 'devops-consulting',
    secondaryServiceName: 'DevOps Consulting',
    title: 'Webhook Integrations Failing?',
    description:
      "Our team implements reliable webhook handlers with signature verification and error recovery.",
  },
  'webhook-payload-generator': {
    primaryService: 'software-development',
    primaryServiceName: 'Software Development',
    secondaryService: 'devsecops',
    secondaryServiceName: 'DevSecOps Services',
    title: 'Testing Webhook Security?',
    description:
      "Our developers implement secure webhook handlers with HMAC validation and replay protection.",
  },

  // Code & Development Tools
  'diff-checker': {
    primaryService: 'devops-consulting',
    primaryServiceName: 'DevOps Consulting',
    secondaryService: 'software-development',
    secondaryServiceName: 'Software Development',
    title: 'Code Review Bottlenecks?',
    description:
      "Our team implements CI/CD pipelines with automated code review, testing, and deployment.",
  },
  'uuid-generator': {
    primaryService: 'software-development',
    primaryServiceName: 'Software Development',
    title: 'Building Distributed Systems?',
    description:
      "From UUIDs to microservices, our team builds scalable, distributed applications.",
  },
  'regex-tester': {
    primaryService: 'software-development',
    primaryServiceName: 'Software Development',
    title: 'Complex Parsing Requirements?',
    description:
      "From log analysis to data extraction, our developers build reliable parsing solutions at scale.",
  },
  'sql-formatter': {
    primaryService: 'software-development',
    primaryServiceName: 'Software Development',
    secondaryService: 'cloud-infrastructure-management',
    secondaryServiceName: 'Cloud Infrastructure & Optimization',
    title: 'Database Performance Issues?',
    description:
      "Slow queries hurt user experience. Our team optimizes database performance and query efficiency.",
  },
  'sql-converter': {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    secondaryService: 'software-development',
    secondaryServiceName: 'Software Development',
    title: 'Database Migration Project?',
    description:
      "Our team handles database migrations across platforms with minimal downtime.",
  },
  'url-encoder': {
    primaryService: 'software-development',
    primaryServiceName: 'Software Development',
    secondaryService: 'devsecops',
    secondaryServiceName: 'DevSecOps Services',
    title: 'Building Secure Web Applications?',
    description:
      "Our developers implement proper encoding, input validation, and XSS prevention.",
  },
  'html-encoder': {
    primaryService: 'devsecops',
    primaryServiceName: 'DevSecOps Services',
    secondaryService: 'software-development',
    secondaryServiceName: 'Software Development',
    title: 'Preventing XSS Vulnerabilities?',
    description:
      "Our DevSecOps team implements secure output encoding and input validation.",
  },
  'sri-hash-generator': {
    primaryService: 'devsecops',
    primaryServiceName: 'DevSecOps Services',
    secondaryService: 'software-development',
    secondaryServiceName: 'Software Development',
    title: 'Securing Third-Party Scripts?',
    description:
      "Our DevSecOps team implements SRI, CSP, and other defenses against supply chain attacks.",
  },
  'markdown-preview': {
    primaryService: 'software-development',
    primaryServiceName: 'Software Development',
    title: 'Building Documentation Systems?',
    description:
      "Our team creates documentation portals, wikis, and knowledge bases for your projects.",
  },

  // Time & Scheduling Tools
  'unix-timestamp-converter': {
    primaryService: 'software-development',
    primaryServiceName: 'Software Development',
    secondaryService: 'managed-services',
    secondaryServiceName: '24/7 IT Monitoring',
    title: 'Debugging Timestamp Issues?',
    description:
      "Our developers handle timezone complexity, timestamp formatting, and time-series data.",
  },
  'cron-builder': {
    primaryService: 'devops-consulting',
    primaryServiceName: 'DevOps Consulting',
    secondaryService: 'managed-services',
    secondaryServiceName: '24/7 IT Monitoring',
    title: 'Job Scheduling Complexity?',
    description:
      "Our DevOps team implements reliable job scheduling, monitoring, and alerting for critical tasks.",
  },
  'world-clock': {
    primaryService: 'software-development',
    primaryServiceName: 'Software Development',
    title: 'Coordinating Global Teams?',
    description:
      "Our distributed team builds applications for international users with proper timezone handling.",
  },

  // Git & Version Control Tools
  'git-command-reference': {
    primaryService: 'devops-consulting',
    primaryServiceName: 'DevOps Consulting',
    secondaryService: 'software-development',
    secondaryServiceName: 'Software Development',
    title: 'Improving Git Workflows?',
    description:
      "Our DevOps team implements branching strategies, CI/CD pipelines, and collaboration workflows.",
  },
  'git-branch-visualizer': {
    primaryService: 'devops-consulting',
    primaryServiceName: 'DevOps Consulting',
    secondaryService: 'software-development',
    secondaryServiceName: 'Software Development',
    title: 'Git Strategy Confusion?',
    description:
      "Our team implements branching models, release processes, and merge strategies for your team.",
  },
  'gitignore-generator': {
    primaryService: 'devops-consulting',
    primaryServiceName: 'DevOps Consulting',
    secondaryService: 'devsecops',
    secondaryServiceName: 'DevSecOps Services',
    title: 'Sensitive Files in Git History?',
    description:
      "Our team implements proper .gitignore patterns, secrets scanning, and git history cleanup.",
  },

  // Config & Environment Tools
  'env-file-generator': {
    primaryService: 'devsecops',
    primaryServiceName: 'DevSecOps Services',
    secondaryService: 'devops-consulting',
    secondaryServiceName: 'DevOps Consulting',
    title: 'Environment Variables Scattered?',
    description:
      "Our DevSecOps team implements centralized secrets management with HashiCorp Vault or cloud-native solutions.",
  },
  'package-json-generator': {
    primaryService: 'software-development',
    primaryServiceName: 'Software Development',
    secondaryService: 'devsecops',
    secondaryServiceName: 'DevSecOps Services',
    title: 'Node.js Project Setup?',
    description:
      "Our developers set up Node.js projects with security best practices and proper dependency management.",
  },
  'requirements-txt-generator': {
    primaryService: 'software-development',
    primaryServiceName: 'Software Development',
    secondaryService: 'devsecops',
    secondaryServiceName: 'DevSecOps Services',
    title: 'Python Dependency Management?',
    description:
      "Our team implements dependency pinning, vulnerability scanning, and virtual environment best practices.",
  },

  // PowerShell & Admin Tools
  'exchange-online-powershell': {
    primaryService: 'productivity-suite-management',
    primaryServiceName: 'Productivity Suite Management',
    secondaryService: 'small-business-cybersecurity',
    secondaryServiceName: 'Small Business Cybersecurity',
    title: 'Managing Exchange Online at Scale?',
    description:
      "Our M365 experts automate mailbox management, security policies, and compliance configurations.",
  },
  'windows-update-powershell': {
    primaryService: 'managed-services',
    primaryServiceName: 'Managed IT Services',
    secondaryService: 'managed-services',
    secondaryServiceName: '24/7 IT Monitoring',
    title: 'Windows Update Headaches?',
    description:
      "Our managed services team automates patching across your Windows infrastructure.",
  },
  'virsh-command-builder': {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    secondaryService: 'devops-consulting',
    secondaryServiceName: 'DevOps Consulting',
    title: 'Operating KVM Infrastructure at Scale?',
    description:
      "Our infrastructure team designs, automates, and supports reliable Linux virtualization environments.",
  },
  'azure-ad-powershell': {
    primaryService: 'productivity-suite-management',
    primaryServiceName: 'Productivity Suite Management',
    secondaryService: 'vciso',
    secondaryServiceName: 'vCISO Services',
    title: 'Azure AD Management Complexity?',
    description:
      "Our team automates identity management, access reviews, and security policies in Entra ID.",
  },
  'aws-s3-command-generator': {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    secondaryService: 'devops-consulting',
    secondaryServiceName: 'DevOps Consulting',
    title: 'AWS S3 Management Challenges?',
    description:
      "Our cloud team optimizes S3 configurations, implements lifecycle policies, and secures buckets.",
  },

  // SLA & Reliability Tools
  'sla-slo-calculator': {
    primaryService: 'devops-consulting',
    primaryServiceName: 'DevOps Consulting',
    secondaryService: 'managed-services',
    secondaryServiceName: '24/7 IT Monitoring',
    title: 'SRE & Reliability Engineering',
    description:
      "Our DevOps team implements SLOs, error budgets, and reliability practices based on Google SRE principles.",
  },
  'rate-limit-calculator': {
    primaryService: 'software-development',
    primaryServiceName: 'Software Development',
    secondaryService: 'cloud-infrastructure-management',
    secondaryServiceName: 'Cloud Infrastructure & Optimization',
    title: 'API Rate Limiting Issues?',
    description:
      "Our developers implement proper rate limiting, caching, and API gateway configurations.",
  },
  'semver-calculator': {
    primaryService: 'devops-consulting',
    primaryServiceName: 'DevOps Consulting',
    secondaryService: 'software-development',
    secondaryServiceName: 'Software Development',
    title: 'Release Management Complexity?',
    description:
      "Our DevOps team implements semantic versioning, release automation, and changelog generation.",
  },

  // Misc Developer Tools
  'http-status-codes': {
    primaryService: 'software-development',
    primaryServiceName: 'Software Development',
    secondaryService: 'devops-consulting',
    secondaryServiceName: 'DevOps Consulting',
    title: 'API Error Handling Questions?',
    description:
      "Our developers build robust APIs with proper status codes, error messages, and documentation.",
  },
  'user-agent-parser': {
    primaryService: 'software-development',
    primaryServiceName: 'Software Development',
    secondaryService: 'managed-services',
    secondaryServiceName: '24/7 IT Monitoring',
    title: 'Analyzing Traffic Patterns?',
    description:
      "Our team implements analytics, bot detection, and traffic analysis for your applications.",
  },
  'lorem-ipsum-generator': {
    primaryService: 'software-development',
    primaryServiceName: 'Software Development',
    title: 'Building UI Prototypes?',
    description:
      "Our developers create polished user interfaces with proper design systems and components.",
  },
  'ascii-art-generator': {
    primaryService: 'software-development',
    primaryServiceName: 'Software Development',
    title: 'Adding Personality to CLIs?',
    description:
      "Our developers build command-line tools with great user experience and documentation.",
  },
  'word-counter': {
    primaryService: 'software-development',
    primaryServiceName: 'Software Development',
    title: 'Building Content Tools?',
    description:
      "Our team creates content management systems, editors, and publishing platforms.",
  },
  'qr-code-generator': {
    primaryService: 'software-development',
    primaryServiceName: 'Software Development',
    title: 'Integrating QR Codes?',
    description:
      "Our developers integrate QR codes for authentication, payments, and marketing campaigns.",
  },
  'color-picker': {
    primaryService: 'software-development',
    primaryServiceName: 'Software Development',
    title: 'Building Design Tools?',
    description:
      "Our team creates design systems, theming engines, and visual configuration tools.",
  },
  'tld-enumerator': {
    primaryService: 'compliance',
    primaryServiceName: 'Compliance & Risk Assessment',
    secondaryService: 'penetration-testing',
    secondaryServiceName: 'Penetration Testing',
    title: 'Protecting Your Brand Online?',
    description:
      "Our team monitors for domain squatting, implements defensive registrations, and takes down threats.",
  },
  'mock-data-generator': {
    primaryService: 'software-development',
    primaryServiceName: 'Software Development',
    secondaryService: 'devsecops',
    secondaryServiceName: 'DevSecOps Services',
    title: 'Need Realistic Test Data?',
    description:
      "Our developers create data masking, synthetic data generation, and test environment management.",
  },
  'llm-token-counter': {
    primaryService: 'software-development',
    primaryServiceName: 'Software Development',
    secondaryService: 'cloud-infrastructure-management',
    secondaryServiceName: 'Cloud Infrastructure & Optimization',
    title: 'Building AI Applications?',
    description:
      "Our team integrates LLMs, optimizes token usage, and builds intelligent applications.",
  },

  // Image & Media Tools
  'webp-to-gif-converter': {
    primaryService: 'software-development',
    primaryServiceName: 'Software Development',
    title: 'Optimizing Media Assets?',
    description:
      "Our developers implement image optimization, CDN configuration, and media processing pipelines.",
  },
  'image-to-pdf-converter': {
    primaryService: 'software-development',
    primaryServiceName: 'Software Development',
    title: 'Document Processing Needs?',
    description:
      "Our team builds document generation, PDF workflows, and file conversion systems.",
  },
  'heic-to-jpg-converter': {
    primaryService: 'software-development',
    primaryServiceName: 'Software Development',
    title: 'Handling Mobile Photo Uploads?',
    description:
      "Our developers implement format conversion, image processing, and upload handling.",
  },
  'image-format-converter': {
    primaryService: 'software-development',
    primaryServiceName: 'Software Development',
    secondaryService: 'cloud-infrastructure-management',
    secondaryServiceName: 'Cloud Infrastructure & Optimization',
    title: 'Image Processing at Scale?',
    description:
      "Our team builds image transformation pipelines with CDN optimization and lazy loading.",
  },
  'file-metadata-analyzer': {
    primaryService: 'devsecops',
    primaryServiceName: 'DevSecOps Services',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance Services',
    title: 'Metadata Leaking Sensitive Info?',
    description:
      "Our team implements metadata stripping, EXIF removal, and privacy-preserving file handling.",
  },
  'book-page-size-converter': {
    primaryService: 'software-development',
    primaryServiceName: 'Software Development',
    title: 'Publishing Automation Needs?',
    description:
      "Our developers build document processing, formatting, and publishing automation tools.",
  },

  // ============================================
  // PLANNING TOOLS
  // ============================================

  'cybersecurity-budget-calculator': {
    primaryService: 'vciso',
    primaryServiceName: 'vCISO Services',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance & Risk Assessment',
    title: 'Building Your Security Budget?',
    description:
      "Our vCISO team helps prioritize investments, justify budgets, and align security with business goals.",
  },
  'data-breach-cost-calculator': {
    primaryService: 'compliance',
    primaryServiceName: 'Compliance & Risk Assessment',
    secondaryService: 'vciso',
    secondaryServiceName: 'vCISO Services',
    title: 'Quantifying Breach Risk?',
    description:
      "Our team performs risk quantification to help you prioritize security investments.",
  },
  'cybersecurity-roi-calculator': {
    primaryService: 'vciso',
    primaryServiceName: 'vCISO Services',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance & Risk Assessment',
    title: 'Justifying Security Spending?',
    description:
      "Our vCISO team builds business cases that resonate with executives and board members.",
  },
  'cloud-cost-comparison': {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    secondaryService: 'cloud-infrastructure-management',
    secondaryServiceName: 'Cloud Infrastructure & Optimization',
    title: 'Cloud Costs Out of Control?',
    description:
      "Our FinOps team optimizes cloud spending with rightsizing, reserved instances, and cost allocation.",
  },
  'cloud-carbon-footprint-estimator': {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    secondaryService: 'vciso',
    secondaryServiceName: 'vCISO Services',
    title: 'Reducing Cloud Carbon Impact?',
    description:
      "Our team optimizes workloads for efficiency, selects green regions, and tracks sustainability metrics.",
  },
  'route53-pricing-calculator': {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    secondaryService: 'cloud-infrastructure-management',
    secondaryServiceName: 'Cloud Infrastructure & Optimization',
    title: 'AWS DNS Cost Questions?',
    description:
      "Our cloud architects optimize DNS configurations, implement failover, and reduce Route 53 costs.",
  },
  'aws-bedrock-pricing-calculator': {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    secondaryService: 'software-development',
    secondaryServiceName: 'Software Development',
    title: 'Planning AI/ML Workloads?',
    description:
      "Our team designs cost-effective AI architectures with prompt optimization and model selection.",
  },
  'backup-recovery-time-calculator': {
    primaryService: 'vciso',
    primaryServiceName: 'vCISO Services',
    secondaryService: 'cloud-infrastructure-management',
    secondaryServiceName: 'Cloud Infrastructure & Optimization',
    title: 'Backup Strategy Gaps?',
    description:
      "Our team designs backup architectures that meet your RTO/RPO requirements with tested recovery procedures.",
  },
  'vm-resource-calculator': {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    secondaryService: 'cloud-infrastructure-management',
    secondaryServiceName: 'Cloud Infrastructure & Optimization',
    title: 'VM Sizing Guesswork?',
    description:
      "Our cloud architects right-size VMs, optimize reservations, and reduce compute costs.",
  },
  'mtbf-mttr-calculator': {
    primaryService: 'devops-consulting',
    primaryServiceName: 'DevOps Consulting',
    secondaryService: 'managed-services',
    secondaryServiceName: '24/7 IT Monitoring',
    title: 'Improving System Reliability?',
    description:
      "Our SRE team implements observability, incident management, and reliability engineering practices.",
  },
  'time-duration-calculator': {
    primaryService: 'vciso',
    primaryServiceName: 'vCISO Services',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance Services',
    title: 'Planning Disaster Recovery?',
    description:
      "Our team designs DR strategies with tested procedures and documented recovery timelines.",
  },
  'timeline-generator': {
    primaryService: 'vciso',
    primaryServiceName: 'vCISO Services',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance Services',
    title: 'Planning a Security Program?',
    description:
      "Our vCISO team creates roadmaps, tracks milestones, and manages security program delivery.",
  },
  'quantitative-risk-analysis': {
    primaryService: 'compliance',
    primaryServiceName: 'Compliance & Risk Assessment',
    secondaryService: 'vciso',
    secondaryServiceName: 'vCISO Services',
    title: 'Need Data-Driven Risk Analysis?',
    description:
      "Our team performs quantitative risk assessments using FAIR methodology and industry data.",
  },
  'pentest-scoping-calculator': {
    primaryService: 'penetration-testing',
    primaryServiceName: 'Penetration Testing',
    secondaryService: 'vciso',
    secondaryServiceName: 'vCISO Services',
    title: 'Planning a Penetration Test?',
    description:
      "Our offensive security team scopes and executes penetration tests with detailed remediation guidance.",
  },
  'dr-site-cost-analyzer': {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    secondaryService: 'vciso',
    secondaryServiceName: 'vCISO Services',
    title: 'Evaluating DR Options?',
    description:
      "Our architects design cost-effective disaster recovery solutions aligned with your RTO requirements.",
  },
  'physical-security-planner': {
    primaryService: 'vciso',
    primaryServiceName: 'vCISO Services',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance Services',
    title: 'Physical Security Gaps?',
    description:
      "Our vCISO team designs physical security programs covering access control, surveillance, and environmental controls.",
  },
  'microsoft-security-quote-builder': {
    primaryService: 'productivity-suite-management',
    primaryServiceName: 'Productivity Suite Management',
    secondaryService: 'vciso',
    secondaryServiceName: 'vCISO Services',
    title: 'Optimizing Microsoft Security Licensing?',
    description:
      "Our M365 experts navigate Microsoft licensing, identify overlaps, and optimize your security investment.",
  },

  // ============================================
  // COMPLIANCE TOOLS
  // ============================================

  'gdpr-checker': {
    primaryService: 'compliance',
    primaryServiceName: 'Compliance Services',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance & Risk Assessment',
    title: 'GDPR Compliance Gaps?',
    description:
      "Our compliance team assesses GDPR readiness, implements controls, and prepares documentation.",
  },
  'privacy-policy-generator': {
    primaryService: 'compliance',
    primaryServiceName: 'Compliance Services',
    secondaryService: 'vciso',
    secondaryServiceName: 'vCISO Services',
    title: 'Privacy Compliance Questions?',
    description:
      "Our team implements privacy programs covering GDPR, CCPA, and other regulations.",
  },
  'security-policy-generator': {
    primaryService: 'vciso',
    primaryServiceName: 'vCISO Services',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance Services',
    title: 'Security Policies Outdated?',
    description:
      "Our vCISO team develops comprehensive security policy frameworks aligned with your business.",
  },
  'gdpr-role-retention-mapper': {
    primaryService: 'compliance',
    primaryServiceName: 'Compliance Services',
    secondaryService: 'vciso',
    secondaryServiceName: 'vCISO Services',
    title: 'GDPR Data Mapping Challenges?',
    description:
      "Our team maps data flows, determines controller/processor relationships, and implements retention policies.",
  },
  'hipaa-quick-assessment': {
    primaryService: 'compliance',
    primaryServiceName: 'Compliance Services',
    secondaryService: 'vciso',
    secondaryServiceName: 'vCISO Services',
    title: 'HIPAA Compliance Concerns?',
    description:
      "Our healthcare compliance experts perform gap assessments, implement controls, and prepare for audits.",
  },
  'soc2-gap-analysis': {
    primaryService: 'compliance',
    primaryServiceName: 'Compliance Services',
    secondaryService: 'vciso',
    secondaryServiceName: 'vCISO Services',
    title: 'Pursuing SOC 2 Certification?',
    description:
      "Our team guides you from gap analysis through certification with audit-ready documentation.",
  },
  'vrm-breach-proof-scorecard': {
    primaryService: 'compliance',
    primaryServiceName: 'Compliance & Risk Assessment',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance Services',
    title: 'Vendor Risk Keeping You Up?',
    description:
      "Our team implements vendor risk programs with assessments, monitoring, and remediation tracking.",
  },
  'data-classification-architect': {
    primaryService: 'compliance',
    primaryServiceName: 'Compliance Services',
    secondaryService: 'vciso',
    secondaryServiceName: 'vCISO Services',
    title: 'Data Classification Confusion?',
    description:
      "Our team implements data classification schemes, handling procedures, and DLP controls.",
  },
  'supply-chain-risk-assessor': {
    primaryService: 'compliance',
    primaryServiceName: 'Compliance & Risk Assessment',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance Services',
    title: 'Supply Chain Risks Unchecked?',
    description:
      "Our team assesses supply chain security across vendors, implements monitoring, and manages third-party risk.",
  },

  // ============================================
  // ASSESSMENT TOOLS
  // ============================================

  'cybersecurity-maturity-assessment': {
    primaryService: 'vciso',
    primaryServiceName: 'vCISO Services',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance & Risk Assessment',
    title: 'Know Your Security Maturity?',
    description:
      "Our vCISO team assesses your current state, defines target maturity, and creates improvement roadmaps.",
  },
  'edr-needs-assessment': {
    primaryService: '24-7-detection-and-response',
    primaryServiceName: '24/7 Detection & Response',
    secondaryService: 'vciso',
    secondaryServiceName: 'vCISO Services',
    title: 'Choosing Endpoint Protection?',
    description:
      "Our team evaluates your needs, recommends solutions, and provides managed detection and response.",
  },
  'cicd-security-checklist': {
    primaryService: 'devsecops',
    primaryServiceName: 'DevSecOps Services',
    secondaryService: 'devops-consulting',
    secondaryServiceName: 'DevOps Consulting',
    title: 'CI/CD Pipeline Security Gaps?',
    description:
      "Our DevSecOps team audits pipelines, implements security gates, and hardens your software delivery.",
  },
  'cloud-security-self-assessment': {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    secondaryService: 'vciso',
    secondaryServiceName: 'vCISO Services',
    title: 'Cloud Security Posture Unknown?',
    description:
      "Our team assesses cloud configurations, implements guardrails, and monitors for drift.",
  },
  'interactive-ransomware-resilience-assessment': {
    primaryService: 'vciso',
    primaryServiceName: 'vCISO Services',
    secondaryService: '24-7-detection-and-response',
    secondaryServiceName: '24/7 Detection & Response',
    title: 'Ransomware Recovery Gaps?',
    description:
      "Our team tests backup recovery, hardens defenses, and prepares incident response procedures.",
  },
  'risk-matrix-calculator': {
    primaryService: 'compliance',
    primaryServiceName: 'Compliance & Risk Assessment',
    secondaryService: 'vciso',
    secondaryServiceName: 'vCISO Services',
    title: 'Risk Assessment Framework Needed?',
    description:
      "Our team implements risk management programs with registers, assessments, and treatment plans.",
  },
  'business-impact-calculator': {
    primaryService: 'vciso',
    primaryServiceName: 'vCISO Services',
    secondaryService: 'compliance',
    secondaryServiceName: 'Compliance Services',
    title: 'Business Continuity Planning?',
    description:
      "Our team conducts BIA, develops BC/DR plans, and tests recovery procedures.",
  },

  // ============================================
  // MEDIA TOOLS
  // ============================================

  'wordcloud-generator': {
    primaryService: 'software-development',
    primaryServiceName: 'Software Development',
    title: 'Building Data Visualizations?',
    description:
      "Our developers create interactive dashboards, reports, and visual analytics tools.",
  },
  'image-compressor': {
    primaryService: 'cloud-infrastructure-management',
    primaryServiceName: 'Cloud Infrastructure & Optimization',
    secondaryService: 'software-development',
    secondaryServiceName: 'Software Development',
    title: 'Website Performance Issues?',
    description:
      "Our team optimizes images, implements CDNs, and improves page load times.",
  },
};

/**
 * Get service mapping for a Knowledge Base article
 */
export function getKBServiceMapping(
  categorySlug: string,
  articleSlug?: string
): ServiceMapping | undefined {
  // Check for specific page mapping first
  if (articleSlug && specificPageMappings[articleSlug]) {
    return specificPageMappings[articleSlug];
  }
  // Fall back to category mapping
  return kbCategoryMappings[categorySlug];
}

/**
 * Get service mapping for a Tool page
 */
export function getToolServiceMapping(
  categorySlug: string,
  toolSlug?: string
): ServiceMapping | undefined {
  // Check for specific page mapping first
  if (toolSlug && specificPageMappings[toolSlug]) {
    return specificPageMappings[toolSlug];
  }
  // Fall back to category mapping
  return toolCategoryMappings[categorySlug];
}

/**
 * Get service mapping for a Blog post
 */
export function getBlogServiceMapping(
  categorySlug: string,
  postSlug?: string
): ServiceMapping | undefined {
  // Check for specific page mapping first
  if (postSlug && specificPageMappings[postSlug]) {
    return specificPageMappings[postSlug];
  }
  // Fall back to category mapping
  return blogCategoryMappings[categorySlug];
}

/**
 * Get default service mapping
 */
export function getDefaultServiceMapping(): ServiceMapping {
  return {
    primaryService: 'secure-mvp',
    primaryServiceName: 'Secure MVP',
    secondaryService: 'software-development',
    secondaryServiceName: 'Custom Software Development',
    title: 'Need help shipping something?',
    description:
      "Productized MVP development for founders. 9 SaaS apps shipped — yours could be next, in 6 weeks.",
  };
}
